import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { API, BACKEND_URL } from '@/utils/constants';
import { formatDate, todayLocalISO, dateToLocalISO } from '@/utils/date';
import { SignatureCanvas } from '@/components/common/SignatureCanvas';
import { AccessoryPicker } from '@/components/common/AccessoryPicker';
import { ReferredByAutocomplete } from '@/components/common/ReferredByAutocomplete';
import { HelpTooltip } from '@/components/HelpTooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { printJobSticker } from '@/utils/sticker';
import { advanceOnEnter } from '@/utils/keyboard';

/**
 * Returns the configured options list with the currently-stored value
 * inserted at the top (marked as legacy) if it is not present.
 * This prevents the dropdown from rendering blank when a stored value
 * has been removed/renamed from Settings.
 */
const optionsWithFallback = (list, currentValue) => {
  const arr = Array.isArray(list) ? list : [];
  if (currentValue && !arr.some((v) => v === currentValue)) {
    return [{ value: currentValue, legacy: true }, ...arr.map((v) => ({ value: v, legacy: false }))];
  }
  return arr.map((v) => ({ value: v, legacy: false }));
};

/**
 * Job Detail Page Component
 * View and manage individual job details, payments, stock allocations, and more
 */
// Parse a backend ISO timestamp safely. When Mongo returns a naive datetime,
// FastAPI serialises it via .isoformat() WITHOUT a timezone marker — JavaScript
// then treats the string as LOCAL time, causing the signature timestamp to
// appear 10 hours off in Melbourne. This helper always coerces such values to
// UTC before formatting, and renders in en-AU local time to match the rest of
// the UI (email history, activity log).
const formatUtcLocal = (iso) => {
  if (!iso) return 'Unknown date';
  const s = String(iso).trim();
  // If the string already carries a timezone marker (Z, +HH:MM, -HH:MM), let
  // Date parse as-is. Otherwise assume UTC and append 'Z'.
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasTz ? s : s + 'Z');
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
};

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  
  // Issue Summary & Quote state
  const [quoteText, setQuoteText] = useState('');
  const [quoteAmount, setQuoteAmount] = useState('');
  const [savingQuote, setSavingQuote] = useState(false);
  const [emailingQuote, setEmailingQuote] = useState(false);
  const [showQuoteHistory, setShowQuoteHistory] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [signatureLink, setSignatureLink] = useState('');
  const signatureLinkInputRef = useRef(null);
  // Tracks whether fetchJob has run at least once — used to avoid overwriting
  // in-progress form state on subsequent re-fetches triggered by other actions.
  const jobRef = useRef(null);
  
  // Payment tracking state
  const [showAddPayment, setShowAddPayment] = useState(false);
  // When set, the Add/Edit Payment form is in EDIT mode and will PUT to
  // /payments/{id} on submit instead of POSTing a new payment. `null` = add mode.
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: todayLocalISO(),
    payment_method: 'Card',
    reference_number: '',
    notes: ''
  });

  // Quick edit state for status and priority
  const [quickStatus, setQuickStatus] = useState('');
  const [quickPriority, setQuickPriority] = useState('');
  // Job Progress Status — operational status independent of the customer-facing `status`.
  const [quickProgressStatus, setQuickProgressStatus] = useState('Not Started');
  const [hasQuickChanges, setHasQuickChanges] = useState(false);
  const [savingQuickEdit, setSavingQuickEdit] = useState(false);

  // Feb 2026 — Fast Date Due editor. Two entry paths:
  //   • 'approval' — auto-opened when user flips Status → Approved on the Job Card
  //   • 'inline'   — user clicked the Date Due value/pencil on the Job Card
  // Both write date_due via PUT /api/jobs/{id}. The 'approval' path also saves
  // the pending status change atomically so there's no lingering "Save Changes"
  // button afterwards.
  const [dateDueDialogOpen, setDateDueDialogOpen] = useState(false);
  const [dateDueDialogValue, setDateDueDialogValue] = useState('');
  const [dateDueDialogContext, setDateDueDialogContext] = useState('inline'); // 'approval' | 'inline'
  const [savingDateDue, setSavingDateDue] = useState(false);

  // Stock allocation state
  const [showAddStock, setShowAddStock] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [stockForm, setStockForm] = useState({
    stock_item_id: '',
    quantity: 1,
    sell_price: 0
  });

  // Invoice state
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    work_performed: '',
    invoice_terms: 'Payment due upon receipt'
  });
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  // Editable invoice email fields (pre-filled from settings template on open)
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  // Feb 2026 — which invoice template the current send is using. Set by
  // handleEmailInvoice (auto-selects paid_invoice_email when paid); user can
  // change via dropdown in the confirm dialog.
  const [invoiceTemplateKey, setInvoiceTemplateKey] = useState('invoice_email');

  // Feb 2026 — Review-request confirm dialog state.
  const [showReviewConfirm, setShowReviewConfirm] = useState(false);
  const [reviewPreview, setReviewPreview] = useState(null);
  const [sendingReview, setSendingReview] = useState(false);
  
  // Adjust amount state
  const [showAdjustAmount, setShowAdjustAmount] = useState(false);
  const [adjustAmountForm, setAdjustAmountForm] = useState({
    new_amount: '',
    reason: ''
  });
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  
  const [emailComposer, setEmailComposer] = useState({
    subject: '',
    body: '',
    cc: '',
    bcc: '',
  });
  // Which template (by id) is currently selected in the Email Customer dialog.
  // "" = the "-- Custom / None --" fallback so the composer keeps whatever the
  // user has typed rather than being clobbered by a template refill.
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');
  const [sendingJobEmail, setSendingJobEmail] = useState(false);

  // SMS state
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);

  // iPad Kiosk state
  const [sendingToKiosk, setSendingToKiosk] = useState(false);
  const [kioskSignedJob, setKioskSignedJob] = useState(null);

  // Internal Notes state
  const [internalNotesOpen, setInternalNotesOpen] = useState(false);
  const [internalNotesForm, setInternalNotesForm] = useState({
    internal_notes: '',
    imaging_location: ''
  });
  const [savingInternalNotes, setSavingInternalNotes] = useState(false);

  // Email preview drill-down — shows the exact body that was sent to the customer
  const [emailPreview, setEmailPreview] = useState({ open: false, log: null });
  // Which "view" of the email is currently shown in the preview dialog:
  //  - 'message' → the text body (template + placeholders, e.g. bank details)
  //  - 'invoice' → the invoice HTML that was embedded/attached
  // For invoice emails both are captured; the toggle lets the operator see
  // exactly what the customer received without losing either half.
  const [emailPreviewView, setEmailPreviewView] = useState('message');
  // Track which email history entry is currently being resent (by storage index)
  const [resendingEmailIndex, setResendingEmailIndex] = useState(null);

  // Work & Billing state
  const [billingForm, setBillingForm] = useState({
    work_performed: '',
    labor_amount: 0,
    emergency_service_amount: 0,
    discount_percent: 0,
    line_items: [],  // [{id, description, category, quantity, unit_price, total}]
  });
  const [savingBilling, setSavingBilling] = useState(false);

  useEffect(() => {
    fetchJob();
    fetchSettings();
    fetchStockItems();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`);
      const previousJob = jobRef.current;
      jobRef.current = response.data;
      setJob(response.data);
      setFormData(response.data);

      // Smart form reset: only overwrite billingForm/internalNotesForm if the
      // user hasn't made unsaved edits. We detect "dirty" by comparing the
      // current form value against the *previous* DB value (jobRef before
      // updating). If they match, the form is clean and safe to refresh.
      const isClean = (formValue, prevDbValue) => {
        if (previousJob === null) return true; // first load
        return formValue === (prevDbValue ?? '') || formValue === prevDbValue;
      };

      setInternalNotesForm(prev => {
        // internal_notes is now an array (managed via dedicated endpoints), so the
        // textarea is purely a "compose new note" input — never reset it from server data.
        const locClean = isClean(prev.imaging_location, previousJob?.imaging_location || '');
        return {
          internal_notes: prev.internal_notes || '',
          imaging_location: locClean ? (response.data.imaging_location || '') : prev.imaging_location,
        };
      });

      setBillingForm(prev => {
        const workClean = isClean(prev.work_performed, previousJob?.work_performed || '');
        const prevLabor = previousJob?.labor_amount ?? 0;
        const laborClean = parseFloat(prev.labor_amount) === parseFloat(prevLabor) || previousJob === null;
        const prevEmergency = previousJob?.emergency_service_amount ?? 0;
        const emergencyClean = parseFloat(prev.emergency_service_amount) === parseFloat(prevEmergency) || previousJob === null;
        const prevDiscount = previousJob?.discount_percent ?? 0;
        const discountClean = parseFloat(prev.discount_percent) === parseFloat(prevDiscount) || previousJob === null;
        // Line items: consider "clean" when the local edit set is deep-equal to
        // the server's previous set (order-sensitive, keyed by id). This lets a
        // background refetch overwrite ONLY when the user hasn't started editing.
        const stringify = (arr) => JSON.stringify(
          (arr || []).map(li => ({
            id: li.id || null,
            d: (li.description || '').trim(),
            c: li.category || 'Other',
            q: parseFloat(li.quantity) || 0,
            u: parseFloat(li.unit_price) || 0,
          }))
        );
        const prevLI = previousJob?.line_items ?? [];
        const lineItemsClean = stringify(prev.line_items) === stringify(prevLI) || previousJob === null;
        if (!workClean && !laborClean && !emergencyClean && !discountClean && !lineItemsClean) return prev;
        return {
          work_performed: workClean ? (response.data.work_performed || '') : prev.work_performed,
          labor_amount: laborClean ? (response.data.labor_amount || 0) : prev.labor_amount,
          emergency_service_amount: emergencyClean ? (response.data.emergency_service_amount || 0) : prev.emergency_service_amount,
          discount_percent: discountClean ? (response.data.discount_percent || 0) : prev.discount_percent,
          line_items: lineItemsClean ? (response.data.line_items || []) : prev.line_items,
        };
      });

      setQuickStatus(response.data.status || '');
      setQuickPriority(response.data.priority || 'Standard');
      setQuickProgressStatus(response.data.progress_status || 'Not Started');
      setHasQuickChanges(false);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to load job');
      navigate('/');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Send SMS function
  const sendSms = async () => {
    if (!job || !smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setSendingSms(true);
    try {
      await axios.post(`${API}/sms/send`, {
        job_id: job.id,
        message: smsMessage
      });
      toast.success(`SMS sent to ${job.customer_name}`);
      setSmsModalOpen(false);
      setSmsMessage('');
      fetchJob(); // Reload to show SMS in history
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // Apply SMS template
  const applySmsTemplate = (template) => {
    if (!job || !settings) return;
    // Template body is stored as `template.message` (settings page saves it under that key).
    // Use replaceAll so a variable like {customer_name} that appears twice gets replaced both times.
    let message = template.message || template.content || '';
    message = message.replaceAll('{customer_name}', job.customer_name || '');
    message = message.replaceAll('{job_number}', job.job_number || '');
    message = message.replaceAll('{status}', job.status || '');
    message = message.replaceAll('{balance_owing}', parseFloat(job.balance_owing || 0).toFixed(2));
    message = message.replaceAll('{business_name}', settings.business_info?.business_name || settings.business_info?.name || '');
    message = message.replaceAll('{business_phone}', settings.business_info?.phone || '');
    setSmsMessage(message);
  };

  // Send to iPad Kiosk
  const sendToKiosk = async () => {
    if (!job) return;
    setSendingToKiosk(true);
    try {
      await axios.post(`${API}/kiosk/send/${job.id}`);
      toast.success(`Job #${job.job_number} sent to iPad for signature`, {
        description: 'Customer can now sign on the iPad'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to iPad');
    } finally {
      setSendingToKiosk(false);
    }
  };

  // Check for kiosk signature (poll for notification)
  useEffect(() => {
    if (!job) return;
    
    let lastSeenSignedAt = null;
    
    const checkKioskSignature = async () => {
      try {
        const response = await axios.get(`${API}/kiosk/last-signed`);
        const signed = response.data.signed_job;
        if (!signed || signed.job_number !== job.job_number) return;
        
        // Only act if this is a NEW signature we haven't already processed.
        // Initialize the baseline on first run so we don't trigger a refetch
        // for an already-existing signature when the page loads.
        const signedAt = signed.signed_at || signed.signature_received_at || signed.updated_at || null;
        if (lastSeenSignedAt === null) {
          lastSeenSignedAt = signedAt || 'initialized';
          return;
        }
        if (signedAt === lastSeenSignedAt) return;
        lastSeenSignedAt = signedAt;
        
        // New signature — notify and refresh
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleik7teSHWDE=');
          audio.volume = 0.5;
          audio.play().catch(() => {});
        } catch (e) {}
        
        toast.success(`Customer signed Job #${job.job_number}!`, {
          description: 'Signature has been saved to the job',
          duration: 5000
        });
        setKioskSignedJob(signed);
        fetchJob(); // Reload to show signature
      } catch (error) {
        // Silent fail - polling
      }
    };

    const interval = setInterval(checkKioskSignature, 5000);
    return () => clearInterval(interval);
  }, [job?.job_number]);

  // Save Internal Notes — adds a new timestamped entry and clears the input
  const saveInternalNotes = async () => {
    const noteText = internalNotesForm.internal_notes?.trim() || '';
    const newImagingLocation = internalNotesForm.imaging_location || '';

    // First, save imaging location if it has changed (still uses /jobs/{id})
    if (newImagingLocation !== (job.imaging_location || '')) {
      try {
        await axios.put(`${API}/jobs/${jobId}`, { imaging_location: newImagingLocation });
      } catch (e) {
        toast.error('Failed to save imaging location');
        return;
      }
    }

    // Then, if there's note text, append it as a new timestamped entry
    if (!noteText && newImagingLocation === (job.imaging_location || '')) {
      toast.info('Nothing to save');
      return;
    }

    setSavingInternalNotes(true);
    try {
      if (noteText) {
        await axios.post(`${API}/jobs/${jobId}/internal-notes`, { text: noteText });
        toast.success('Note added');
      } else {
        toast.success('Imaging location saved');
      }
      // Clear the text field after saving so it's ready for the next note
      setInternalNotesForm({
        internal_notes: '',
        imaging_location: newImagingLocation,
      });
      await fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingInternalNotes(false);
    }
  };

  // Delete a specific internal note entry
  const deleteInternalNote = async (noteId) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}/internal-notes/${noteId}`);
      toast.success('Note deleted');
      await fetchJob();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  // Save Work & Billing
  const saveBilling = async () => {
    setSavingBilling(true);
    try {
      const laborAmount = parseFloat(billingForm.labor_amount) || 0;
      const emergencyAmount = parseFloat(billingForm.emergency_service_amount) || 0;
      const stockTotal = parseFloat(job.stock_total) || 0;
      // Normalize line items — recompute totals, drop empty rows.
      const cleanedLineItems = (billingForm.line_items || [])
        .map(li => {
          const qty = parseFloat(li.quantity) || 0;
          const unit = parseFloat(li.unit_price) || 0;
          return {
            id: li.id,
            description: (li.description || '').trim(),
            category: li.category || 'Other',
            quantity: qty,
            unit_price: unit,
            total: parseFloat((qty * unit).toFixed(2)),
          };
        })
        .filter(li => li.description || li.total > 0);
      const lineItemsTotal = cleanedLineItems.reduce((s, li) => s + li.total, 0);
      const discPct = Math.max(0, Math.min(100, parseFloat(billingForm.discount_percent) || 0));
      // Partner discount applies to LABOUR only — stock/emergency/line items never discounted.
      const discAmt = parseFloat((laborAmount * (discPct / 100)).toFixed(2));
      const rrpTotal = laborAmount + stockTotal + emergencyAmount + lineItemsTotal;
      const totalAmount = parseFloat((rrpTotal - discAmt).toFixed(2));  // NET inc GST
      const gstPercentage = parseFloat(job.gst_percentage) || 10;
      const subtotal = totalAmount / (1 + gstPercentage / 100);
      const gstAmount = totalAmount - subtotal;
      
      await axios.put(`${API}/jobs/${jobId}`, {
        work_performed: billingForm.work_performed,
        labor_amount: laborAmount,
        emergency_service_amount: emergencyAmount,
        line_items: cleanedLineItems,
        total_amount: totalAmount,
        subtotal: subtotal,
        gst_amount: gstAmount,
        discount_percent: discPct,
        discount_amount: discAmt,
        rrp_total_amount: rrpTotal,
        balance_owing: totalAmount - (parseFloat(job.amount_paid) || 0)
      });
      toast.success('Billing saved');
      fetchJob();
    } catch (error) {
      toast.error('Failed to save billing');
    } finally {
      setSavingBilling(false);
    }
  };

  // Calculate billing totals (for display)
  // Partner Discount % applies to LABOUR ONLY — stock / emergency / line items
  // are never discounted. Emergency Service and Additional Line Items are all
  // discrete inc-GST invoice rows added to the total after labour (post-discount)
  // + stock.
  const calculateBillingTotals = () => {
    const laborAmount = parseFloat(billingForm.labor_amount) || 0;
    const emergencyAmount = parseFloat(billingForm.emergency_service_amount) || 0;
    const stockTotal = parseFloat(job?.stock_total) || 0;
    const discPct = Math.max(0, Math.min(100, parseFloat(billingForm.discount_percent) || 0));
    const discountAmt = parseFloat((laborAmount * (discPct / 100)).toFixed(2));
    const laborNet = laborAmount - discountAmt;
    // Line items total from local edit state so the Summary updates live as the user types.
    const lineItemsList = (billingForm.line_items || []).map(li => ({
      ...li,
      _total: (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0),
    }));
    const lineItemsTotal = lineItemsList.reduce((s, li) => s + li._total, 0);
    const rrpTotal = laborAmount + stockTotal + emergencyAmount + lineItemsTotal;
    const totalIncGst = laborNet + stockTotal + emergencyAmount + lineItemsTotal;  // discount NEVER affects stock/emergency/line items
    const gstPercentage = parseFloat(job?.gst_percentage) || 10;
    const subtotal = totalIncGst / (1 + gstPercentage / 100);
    const gstAmount = totalIncGst - subtotal;
    
    // Calculate labor breakdown
    const laborExGst = laborAmount / (1 + gstPercentage / 100);
    const laborGst = laborAmount - laborExGst;
    
    // Calculate stock breakdown  
    const stockExGst = stockTotal / (1 + gstPercentage / 100);
    const stockGst = stockTotal - stockExGst;
    
    return {
      laborIncGst: laborAmount,
      laborExGst,
      laborGst,
      laborNet,
      emergencyAmount,
      lineItemsTotal,
      stockTotal,
      stockExGst,
      stockGst,
      rrpTotal,
      discountPercent: discPct,
      discountAmt,
      subtotal,
      gstAmount,
      totalIncGst,
    };
  };

  // Quick Generate Invoice (uses current billing data)
  const handleQuickGenerateInvoice = async () => {
    // First save the billing data
    setSavingBilling(true);
    try {
      const laborAmount = parseFloat(billingForm.labor_amount) || 0;
      const emergencyAmount = parseFloat(billingForm.emergency_service_amount) || 0;
      const stockTotal = parseFloat(job.stock_total) || 0;
      const cleanedLineItems = (billingForm.line_items || [])
        .map(li => {
          const qty = parseFloat(li.quantity) || 0;
          const unit = parseFloat(li.unit_price) || 0;
          return {
            id: li.id,
            description: (li.description || '').trim(),
            category: li.category || 'Other',
            quantity: qty,
            unit_price: unit,
            total: parseFloat((qty * unit).toFixed(2)),
          };
        })
        .filter(li => li.description || li.total > 0);
      const lineItemsTotal = cleanedLineItems.reduce((s, li) => s + li.total, 0);
      const discPct = Math.max(0, Math.min(100, parseFloat(billingForm.discount_percent) || 0));
      // Discount is on LABOUR only — stock/emergency/line items never discounted.
      const discAmt = parseFloat((laborAmount * (discPct / 100)).toFixed(2));
      const rrpTotal = laborAmount + stockTotal + emergencyAmount + lineItemsTotal;
      const totalAmount = parseFloat((rrpTotal - discAmt).toFixed(2));
      const gstPercentage = parseFloat(job.gst_percentage) || 10;
      const subtotal = totalAmount / (1 + gstPercentage / 100);
      const gstAmount = totalAmount - subtotal;
      
      // Save billing first
      await axios.put(`${API}/jobs/${jobId}`, {
        work_performed: billingForm.work_performed,
        labor_amount: laborAmount,
        emergency_service_amount: emergencyAmount,
        line_items: cleanedLineItems,
        total_amount: totalAmount,
        subtotal: subtotal,
        gst_amount: gstAmount,
        discount_percent: discPct,
        discount_amount: discAmt,
        rrp_total_amount: rrpTotal,
        balance_owing: totalAmount - (parseFloat(job.amount_paid) || 0)
      });
      
      // Then generate invoice
      await axios.post(`${API}/jobs/${jobId}/generate-invoice`, null, {
        params: {
          work_performed: billingForm.work_performed,
          invoice_terms: job.invoice_terms || 'Payment due upon receipt'
        }
      });
      
      toast.success('Invoice generated successfully!');
      setShowInvoicePreview(true);
      fetchJob();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    } finally {
      setSavingBilling(false);
    }
  };

  // Calculate job profit
  const calculateJobProfit = () => {
    if (!job) return { revenue: 0, costs: 0, profit: 0, margin: 0 };
    
    const revenue = parseFloat(job.total_amount || 0);
    
    // Calculate stock/parts costs
    const stockCosts = (job.stock_items || []).reduce((total, item) => {
      return total + (parseFloat(item.cost_price || 0) * parseInt(item.quantity || 0));
    }, 0);
    
    const profit = revenue - stockCosts;
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    
    return {
      revenue,
      costs: stockCosts,
      profit,
      margin
    };
  };

  // Quick edit handlers
  const handleQuickStatusChange = (newStatus) => {
    // Feb 2026 — Workflow-aware auto-prompt: when the user flips status to
    // Approved (from any other status) manually, immediately open the Set
    // Date Due dialog. Skips triggering on programmatic reloads (quickStatus
    // === newStatus) and when the job is already Approved.
    const flippingToApproved =
      newStatus === 'Approved' && quickStatus !== 'Approved';
    setQuickStatus(newStatus);
    setHasQuickChanges(true);
    if (flippingToApproved) {
      setDateDueDialogContext('approval');
      setDateDueDialogValue(job?.date_due ? job.date_due.slice(0, 10) : todayLocalISO());
      setDateDueDialogOpen(true);
    }
  };

  const handleQuickPriorityChange = (newPriority) => {
    setQuickPriority(newPriority);
    setHasQuickChanges(true);
  };

  const handleQuickProgressStatusChange = (newProgressStatus) => {
    setQuickProgressStatus(newProgressStatus);
    setHasQuickChanges(true);
  };

  const saveQuickChanges = async () => {
    setSavingQuickEdit(true);
    try {
      await axios.put(`${API}/jobs/${jobId}`, {
        status: quickStatus,
        priority: quickPriority,
        progress_status: quickProgressStatus,
      });
      toast.success('Job updated!');
      setHasQuickChanges(false);
      fetchJob();
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error('Failed to update job');
    } finally {
      setSavingQuickEdit(false);
    }
  };

  // Feb 2026 — Fast Date Due editor handlers.
  // Open the dialog in "inline" mode (user clicked the date on the Job Card).
  const openInlineDateDueDialog = () => {
    setDateDueDialogContext('inline');
    setDateDueDialogValue(job?.date_due ? job.date_due.slice(0, 10) : todayLocalISO());
    setDateDueDialogOpen(true);
  };

  // Save from the Date Due dialog. On the 'approval' path we bundle the
  // pending status/priority/progress changes into the same PUT so there's
  // no lingering "Save Changes" button afterwards. On 'inline' we PUT just
  // date_due to keep the write minimal.
  const saveDateDueFromDialog = async () => {
    setSavingDateDue(true);
    try {
      const payload = { date_due: dateDueDialogValue || null };
      if (dateDueDialogContext === 'approval') {
        payload.status = quickStatus;
        payload.priority = quickPriority;
        payload.progress_status = quickProgressStatus;
      }
      await axios.put(`${API}/jobs/${jobId}`, payload);
      toast.success(
        dateDueDialogContext === 'approval'
          ? 'Job approved and Date Due saved'
          : 'Date Due updated'
      );
      if (dateDueDialogContext === 'approval') setHasQuickChanges(false);
      setDateDueDialogOpen(false);
      fetchJob();
    } catch (error) {
      console.error('Error saving date due:', error);
      toast.error('Failed to save Date Due');
    } finally {
      setSavingDateDue(false);
    }
  };

  // Skip (approval path only) — persist the status change without a date_due,
  // so the user isn't left with a dangling "Save Changes" button.
  const skipDateDueFromDialog = async () => {
    if (dateDueDialogContext !== 'approval') {
      setDateDueDialogOpen(false);
      return;
    }
    setSavingDateDue(true);
    try {
      await axios.put(`${API}/jobs/${jobId}`, {
        status: quickStatus,
        priority: quickPriority,
        progress_status: quickProgressStatus,
      });
      toast.success('Status saved (Date Due skipped)');
      setHasQuickChanges(false);
      setDateDueDialogOpen(false);
      fetchJob();
    } catch (error) {
      console.error('Error saving status:', error);
      toast.error('Failed to save status');
    } finally {
      setSavingDateDue(false);
    }
  };

  // Handle adjust total amount
  const handleAdjustAmount = async (e) => {
    e.preventDefault();
    setSavingAdjustment(true);
    
    const newAmount = parseFloat(adjustAmountForm.new_amount);
    if (isNaN(newAmount) || newAmount < 0) {
      toast.error('Please enter a valid amount');
      setSavingAdjustment(false);
      return;
    }
    
    try {
      await axios.put(`${API}/jobs/${jobId}`, {
        total_amount: newAmount
      });
      
      // Log the adjustment reason if provided (via comment)
      if (adjustAmountForm.reason.trim()) {
        const oldAmount = job.total_amount || 0;
        const adjustmentNote = `💰 Amount adjusted: $${oldAmount.toFixed(2)} → $${newAmount.toFixed(2)}. Reason: ${adjustAmountForm.reason}`;
        await axios.post(`${API}/jobs/${jobId}/comments`, {
          text: adjustmentNote,
          is_private: true
        });
      }
      
      toast.success('Total amount updated successfully');
      setShowAdjustAmount(false);
      setAdjustAmountForm({ new_amount: '', reason: '' });
      fetchJob();
    } catch (error) {
      console.error('Error adjusting amount:', error);
      toast.error('Failed to update amount');
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleUpdate = async () => {
    try {
      // In edit mode, formData.total_amount holds the RRP (what user typed / displayed).
      // Transform: total_amount → net (post-discount); rrp_total_amount preserves RRP.
      const rrp = parseFloat(formData.total_amount) || 0;
      const discPct = Math.max(0, Math.min(100, parseFloat(formData.discount_percent) || 0));
      const discAmt = parseFloat((rrp * (discPct / 100)).toFixed(2));
      const netTotal = parseFloat((rrp - discAmt).toFixed(2));
      const payload = {
        ...formData,
        total_amount: netTotal,
        rrp_total_amount: rrp,
        discount_percent: discPct,
        discount_amount: discAmt,
      };
      await axios.put(`${API}/jobs/${jobId}`, payload);
      toast.success('Job updated successfully!');
      setEditing(false);
      fetchJob();
    } catch (error) {
      console.error('Error updating job:', error);
      let errorMessage = 'Failed to update job';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleOpenEmailComposer = () => {
    // Default open state: no template selected → keep the classic auto-generated
    // "Job details snapshot" body so we don't regress the previous behaviour for
    // users who just want to send the customer their full job info.
    const bizInfo = settings?.business_info || {};
    const subject = `Job #${job.job_number} - ${job.status}`;
    
    const emailBody = `Dear ${job.customer_name},

Thank you for choosing our services. Here are your job details:

Job Number: ${job.job_number}
Job Type: ${job.job_type}
Brand: ${job.brand}
Model: ${job.model_number || 'N/A'}
Status: ${job.status}
Date In: ${formatDate(job.date_in)}
Date Finished: ${formatDate(job.date_finished)}
Date Due: ${formatDate(job.date_due)}

Financial Details:
Total Amount (inc. GST): $${parseFloat(job.total_amount || 0).toFixed(2)}
GST (${parseFloat(job.gst_percentage || 10).toFixed(2)}%): $${parseFloat(job.gst_amount || 0).toFixed(2)}
Subtotal (ex. GST): $${parseFloat(job.subtotal || 0).toFixed(2)}

We will keep you updated on the progress of your job.

Best regards,
${bizInfo.business_name || 'Your Business'}`;

    setSelectedEmailTemplateId('');
    setEmailComposer({ subject, body: emailBody, cc: '', bcc: '' });
    setEmailDialogOpen(true);
  };

  // Substitute the standard placeholder tokens against the current job/settings.
  // Kept as a local helper (rather than a util import) because the token set is
  // narrow and tightly coupled to what the backend seeds into
  // settings.email_templates.customer_templates.
  const applyEmailPlaceholders = (text) => {
    if (!text) return '';
    const biz = settings?.business_info || {};
    const brand = job?.brand || '';
    const model = job?.model_number || '';
    const deviceInfo = [brand, model].filter(Boolean).join(' ').trim() || (job?.job_type || 'your device');
    return text
      .replaceAll('{customer_name}', job?.customer_name || 'Customer')
      .replaceAll('{job_number}', job?.job_number ?? '')
      .replaceAll('{device_info}', deviceInfo)
      .replaceAll('{business_name}', biz.business_name || biz.name || '')
      .replaceAll('{business_phone}', biz.phone || '')
      .replaceAll('{business_email}', biz.email || '');
  };

  // Called when the user picks a template from the dropdown inside the Email
  // Customer dialog. We rewrite BOTH subject + body so it feels like starting
  // fresh with that template; the user can still hand-edit afterwards.
  const handleSelectEmailTemplate = (templateId) => {
    setSelectedEmailTemplateId(templateId);
    if (!templateId) return;  // "-- Custom --" chosen; leave composer untouched
    const tpl = (settings?.email_templates?.customer_templates || []).find((t) => t.id === templateId);
    if (!tpl) return;
    // Prepend the job number so the customer's inbox scan reveals which job
    // the email is about even before opening it. Backend will no-op the
    // prefix if it's already there, so double-prepending is safe.
    const subject = `[Job #${job?.job_number ?? ''}] ${tpl.subject || ''}`.trim();
    setEmailComposer({
      subject,
      body: applyEmailPlaceholders(tpl.body || ''),
      cc:  tpl.default_cc  || '',
      bcc: tpl.default_bcc || '',
    });
  };

  const handleSendJobEmail = async () => {
    if (!emailComposer.subject.trim() || !emailComposer.body.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }

    setSendingJobEmail(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/email-customer`, {
        subject: emailComposer.subject,
        body: emailComposer.body,
        cc: emailComposer.cc,
        bcc: emailComposer.bcc,
        template_id: selectedEmailTemplateId || null,
      });
      toast.success(`✅ Email sent successfully to ${job.email}!`, { duration: 4000 });
      setEmailDialogOpen(false);
      await fetchJob(); // Refresh to show email log
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send email';
      toast.error(`❌ ${errorMsg}`, { duration: 5000 });
    } finally {
      setSendingJobEmail(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setAddingComment(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/comments`, {
        text: newComment,
        follow_up_date: followUpDate || null
      });
      toast.success(followUpDate ? 'Comment and reminder added!' : 'Comment added successfully!');
      setNewComment('');
      setFollowUpDate('');
      fetchJob(); // Reload job to get updated comments
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const handleCompleteReminder = async (commentIndex) => {
    try {
      await axios.put(`${API}/jobs/${jobId}/comments/${commentIndex}/complete`);
      toast.success('Reminder marked as complete!');
      fetchJob(); // Reload job
    } catch (error) {
      console.error('Error completing reminder:', error);
      toast.error('Failed to complete reminder');
    }
  };

  const handleSaveQuote = async () => {
    if (!quoteText.trim()) {
      toast.error('Please enter quote text');
      return;
    }
    
    setSavingQuote(true);
    try {
      const quoteData = { 
        text: quoteText,
        amount: quoteAmount ? parseFloat(quoteAmount) : null
      };
      const response = await axios.post(`${API}/jobs/${jobId}/quote`, quoteData);
      toast.success('Quote saved! Status changed to "Quoted"');
      fetchJob(); // Reload job to get updated quote history and status
      setQuoteText(''); // Clear the textarea
      setQuoteAmount(''); // Clear the amount
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to save quote');
    } finally {
      setSavingQuote(false);
    }
  };

  // Quote email dialog state
  const [showQuoteEmailDialog, setShowQuoteEmailDialog] = useState(false);
  const [quotePersonalMsg, setQuotePersonalMsg] = useState('');
  const [quoteCc, setQuoteCc] = useState('');
  const [quoteBcc, setQuoteBcc] = useState('');
  // Selected custom quote template on the Send Quote dialog. 'standard'
  // means use the default personal_message from settings; anything else
  // is the `id` of a custom template stored in
  // settings.email_templates.quote_custom_templates. When a custom template
  // has {deposit} in its body, quoteDeposit is substituted live.
  const [quoteTemplateId, setQuoteTemplateId] = useState('standard');
  const [quoteDeposit, setQuoteDeposit] = useState('');

  // Substitute standard template placeholders in a body string.
  // Kept separate so it can be reused both for the default personal message
  // (from settings.email_templates.quote_email.body) and for custom quote
  // templates that also use these placeholders.
  const applyQuotePlaceholders = (body, extra = {}) => {
    const biz = settings?.business_info || {};
    return (body || '')
      .replace(/\{customer_name\}/g, job?.customer_name || '')
      .replace(/\{job_number\}/g, job?.job_number ?? '')
      .replace(/\{business_name\}/g, biz.business_name || '')
      .replace(/\{business_phone\}/g, biz.business_phone || biz.phone || '')
      .replace(/\{deposit\}/g, (extra.deposit ?? '').toString());
  };

  const handleEmailQuote = () => {
    // Open the edit-before-send dialog, pre-filling defaults from settings.
    // The "body" template doubles as the default personal message — placeholders
    // ({customer_name}, {job_number}, {business_name}, {business_phone}) are
    // substituted so the user can hit Send without retyping the same intro.
    const tpl = settings?.email_templates?.quote_email || {};
    const defaultBody = applyQuotePlaceholders(tpl.body || '');
    setQuoteTemplateId('standard');
    setQuoteDeposit('');
    setQuotePersonalMsg(defaultBody);
    setQuoteCc(tpl.default_cc || '');
    setQuoteBcc(tpl.default_bcc || '');
    setShowQuoteEmailDialog(true);
  };

  // Called when the user switches template in the Send Quote dialog.
  // Rebuilds the personal-message body from the picked template, substituting
  // {deposit} live if the template uses it. The user can still edit the
  // textarea after switching. Feb 2026 — also switches CC/BCC to the picked
  // template's own default_cc / default_bcc (falling back to the standard
  // quote_email defaults so the user's saved global copy-to-me isn't lost
  // when they haven't customised the per-template BCC yet).
  const switchQuoteTemplate = (templateId) => {
    setQuoteTemplateId(templateId);
    const stdTpl = settings?.email_templates?.quote_email || {};
    if (templateId === 'standard') {
      setQuoteDeposit('');
      setQuotePersonalMsg(applyQuotePlaceholders(stdTpl.body || ''));
      setQuoteCc(stdTpl.default_cc || '');
      setQuoteBcc(stdTpl.default_bcc || '');
      return;
    }
    const customList = settings?.email_templates?.quote_custom_templates || [];
    const picked = customList.find((t) => t.id === templateId);
    if (!picked) return;
    const dep = picked.deposit_default != null ? String(picked.deposit_default) : '';
    setQuoteDeposit(dep);
    setQuotePersonalMsg(applyQuotePlaceholders(picked.body || '', { deposit: dep }));
    setQuoteCc(picked.default_cc != null ? picked.default_cc : (stdTpl.default_cc || ''));
    setQuoteBcc(picked.default_bcc != null ? picked.default_bcc : (stdTpl.default_bcc || ''));
  };

  // Re-render the personal message when the deposit amount is edited while a
  // custom {deposit}-aware template is active. We rebuild from the raw template
  // body so the substitution is idempotent regardless of prior edits to the
  // textarea. If the user has manually edited the textarea we skip the rebuild
  // (best-effort: rebuild only when the current text still matches the last
  // computed version — implemented by tracking the last-rendered value).
  const applyDepositChange = (nextDeposit) => {
    setQuoteDeposit(nextDeposit);
    if (quoteTemplateId === 'standard') return;
    const customList = settings?.email_templates?.quote_custom_templates || [];
    const picked = customList.find((t) => t.id === quoteTemplateId);
    if (!picked) return;
    setQuotePersonalMsg(applyQuotePlaceholders(picked.body || '', { deposit: nextDeposit }));
  };

  const confirmEmailQuote = async () => {
    setShowQuoteEmailDialog(false);
    setEmailingQuote(true);
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/email-quote`, {
        personal_message: quotePersonalMsg,
        cc: quoteCc,
        bcc: quoteBcc,
      });
      toast.success(response.data.message || 'Quote emailed successfully!');
      fetchJob(); // Reload job to update email history
    } catch (error) {
      console.error('Error emailing quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to email quote');
    } finally {
      setEmailingQuote(false);
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleGenerateSignatureLink = async () => {
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/signature-link`);
      const fullUrl = `${BACKEND_URL}/sign/${response.data.signature_token}`;
      setSignatureLink(fullUrl);
      
      // Don't copy immediately - let user click the Copy button after it's displayed
      toast.success('Signature link generated! Click the Copy button below.');
      
    } catch (error) {
      console.error('Error generating signature link:', error);
      toast.error('Failed to generate signature link');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    // Check if payment would exceed total amount
    const paymentAmount = parseFloat(paymentForm.amount) || 0;
    const currentPaid = job.amount_paid || 0;
    const totalAmount = job.total_amount || 0;
    // For edits: subtract the original payment's amount before comparing
    // (otherwise editing $50 → $60 falsely flags overpayment).
    const originalAmount = editingPaymentId
      ? parseFloat((job.payments || []).find((p) => p.id === editingPaymentId)?.amount ?? 0) || 0
      : 0;
    const newTotalPaid = currentPaid - originalAmount + paymentAmount;
    
    if (newTotalPaid > totalAmount && totalAmount > 0) {
      const overpayment = newTotalPaid - totalAmount;
      const confirmed = window.confirm(
        `⚠️ WARNING: This payment will result in an OVERPAYMENT!\n\n` +
        `Total Amount: $${totalAmount.toFixed(2)}\n` +
        `Already Paid: $${(currentPaid - originalAmount).toFixed(2)}\n` +
        `This Payment: $${paymentAmount.toFixed(2)}\n` +
        `New Total Paid: $${newTotalPaid.toFixed(2)}\n` +
        `Overpayment: $${overpayment.toFixed(2)}\n\n` +
        `Are you sure you want to continue?`
      );
      
      if (!confirmed) {
        return; // Don't proceed with payment
      }
    }
    
    try {
      const response = editingPaymentId
        ? await axios.put(`${API}/jobs/${jobId}/payments/${editingPaymentId}`, paymentForm)
        : await axios.post(`${API}/jobs/${jobId}/payments`, paymentForm);
      setJob(response.data);
      setFormData(response.data);
      // If this payment fully closed the job, surface a cash-basis revenue confirmation.
      // The backend sets payment_status="Paid" + auto-stamps date_out the moment the job is fully paid.
      const fullyClosed = response.data?.payment_status === 'Paid' && (response.data?.revenue_date || response.data?.date_out);
      if (editingPaymentId) {
        toast.success('Payment updated successfully!');
      } else if (fullyClosed) {
        const rev = parseFloat(response.data?.revenue_amount ?? response.data?.total_amount ?? 0);
        const revDate = (response.data?.revenue_date || response.data?.date_out || todayLocalISO()).slice(0, 10);
        toast.success(
          `💰 Revenue $${rev.toFixed(2)} recorded for ${revDate} — job closed`,
          { duration: 5000, description: 'This amount now appears in Reports, P&L and BAS.' }
        );
      } else {
        toast.success('Payment added successfully!');
      }
      setShowAddPayment(false);
      setEditingPaymentId(null);
      setPaymentForm({
        amount: '',
        payment_date: todayLocalISO(),
        payment_method: 'Card',
        reference_number: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error(editingPaymentId ? 'Failed to update payment' : 'Failed to add payment');
    }
  };

  // Opens the shared Add/Edit form pre-filled with an existing payment's fields.
  // We reuse the exact same form + submit handler to avoid a second component;
  // the `editingPaymentId` flag toggles between POST (new) and PUT (edit) at
  // submit time. Placement of the form (top of card) means it scrolls into view
  // naturally on click.
  const handleStartEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setPaymentForm({
      amount: String(payment.amount ?? ''),
      // payment_date may already be a Date after backend parsing — normalise to YYYY-MM-DD
      payment_date: payment.payment_date
        ? (typeof payment.payment_date === 'string'
            ? payment.payment_date.slice(0, 10)
            : dateToLocalISO(new Date(payment.payment_date)))
        : todayLocalISO(),
      payment_method: payment.payment_method || 'Cash',
      reference_number: payment.reference_number || '',
      notes: payment.notes || '',
    });
    setShowAddPayment(true);
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      const response = await axios.delete(`${API}/jobs/${jobId}/payments/${paymentId}`);
      setJob(response.data);
      setFormData(response.data);
      toast.success('Payment deleted successfully!');
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment');
    }
  };

  // Stock allocation functions
  const fetchStockItems = async () => {
    try {
      const response = await axios.get(`${API}/stock-items`);
      setStockItems(response.data);
    } catch (error) {
      console.error('Error fetching stock items:', error);
    }
  };

  const handleOpenAddStock = () => {
    setStockForm({
      stock_item_id: '',
      quantity: 1,
      sell_price: 0
    });
    setShowAddStock(true);
  };

  const handleStockItemChange = (stockItemId) => {
    const selectedItem = stockItems.find(item => item.id === stockItemId);
    if (selectedItem) {
      setStockForm({
        ...stockForm,
        stock_item_id: stockItemId,
        sell_price: selectedItem.sell_price || 0
      });
    } else {
      setStockForm({
        ...stockForm,
        stock_item_id: stockItemId
      });
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    
    const selectedItem = stockItems.find(item => item.id === stockForm.stock_item_id);
    if (!selectedItem) {
      toast.error('Please select a stock item');
      return;
    }

    // Check if sufficient quantity available
    if (parseFloat(stockForm.quantity) > selectedItem.current_quantity) {
      toast.error(`Insufficient stock! Only ${selectedItem.current_quantity} units available.`);
      return;
    }

    try {
      const stockData = {
        stock_item_id: stockForm.stock_item_id,
        stock_item_name: selectedItem.item_name,
        brand: selectedItem.brand || '',
        quantity: parseFloat(stockForm.quantity),
        cost_price: selectedItem.cost_price,
        sell_price: parseFloat(stockForm.sell_price)
      };

      await axios.post(`${API}/jobs/${jobId}/stock`, stockData);
      toast.success('Stock item added to job!');
      setShowAddStock(false);
      fetchJob(); // Refresh job data
      fetchStockItems(); // Refresh stock quantities
    } catch (error) {
      console.error('Error adding stock to job:', error);
      // Handle error message properly
      let errorMessage = 'Failed to add stock item';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } else {
            errorMessage = JSON.stringify(error.response.data.detail);
          }
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleRemoveStock = async (stockItemId, lineId) => {
    if (!window.confirm('Are you sure you want to remove this stock item from the job?')) return;
    
    try {
      // Feb 2026 — pass line_id so the backend removes exactly this row
      // even if the same master stock item is present on multiple rows.
      const url = lineId
        ? `${API}/jobs/${jobId}/stock/${stockItemId}?line_id=${encodeURIComponent(lineId)}`
        : `${API}/jobs/${jobId}/stock/${stockItemId}`;
      await axios.delete(url);
      toast.success('Stock item removed from job!');
      fetchJob(); // Refresh job data
      fetchStockItems(); // Refresh stock quantities
    } catch (error) {
      console.error('Error removing stock from job:', error);
      toast.error('Failed to remove stock item');
    }
  };

  // Invoice functions
  const handleOpenInvoiceDialog = () => {
    setInvoiceForm({
      work_performed: job.work_performed || '',
      invoice_terms: job.invoice_terms || 'Payment due upon receipt'
    });
    setShowInvoiceDialog(true);
  };

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/jobs/${jobId}/generate-invoice`, null, {
        params: {
          work_performed: invoiceForm.work_performed,
          invoice_terms: invoiceForm.invoice_terms
        }
      });
      toast.success('Invoice generated successfully!');
      setShowInvoiceDialog(false);
      setShowInvoicePreview(true);
      fetchJob(); // Refresh to get updated invoice data
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    }
  };

  const handlePrintInvoice = () => {
    window.open(`${API}/jobs/${jobId}/invoice-view`, '_blank');
  };

  // Print a single job sticker. Mirrors the logic in Dashboard.jsx / AllJobsPage.jsx
  // so a tech can reprint a damaged/missing sticker without leaving the job card.
  const printSticker = () => printJobSticker(job, settings);

  const handleEmailInvoice = () => {
    if (!job.email) {
      toast.error('Customer email not found on this job');
      return;
    }
    // Feb 2026 — auto-select 'paid_invoice_email' template when job is Paid;
    // operator can override via the dropdown in the confirm dialog.
    const isPaid = (job.payment_status || '').toLowerCase() === 'paid';
    const initialKey = isPaid ? 'paid_invoice_email' : 'invoice_email';
    applyInvoiceTemplate(initialKey);
    setInvoiceTemplateKey(initialKey);
    setShowEmailConfirm(true);
  };

  // Extracted so switching the template dropdown re-fills subject/body/cc/bcc.
  const applyInvoiceTemplate = (key) => {
    const tpl = settings?.email_templates?.[key] || settings?.email_templates?.invoice_email || {};
    const biz = settings?.business_info || {};
    const placeholders = {
      customer_name: job.customer_name || 'Customer',
      job_number: String(job.job_number || ''),
      total_amount: (job.total_amount || 0).toFixed(2),
      business_name: biz.business_name || 'Your Business',
      business_phone: biz.phone || '',
      business_email: biz.email || settings?.email_config?.email || '',
    };
    const render = (s) => {
      let out = s || '';
      Object.entries(placeholders).forEach(([k, v]) => { out = out.split('{' + k + '}').join(String(v)); });
      return out;
    };
    setEmailSubject(render(tpl.subject || `Invoice #${placeholders.job_number} from ${placeholders.business_name}`));
    setEmailBody(render(tpl.body || `Dear ${placeholders.customer_name},\n\nPlease find your invoice attached.\n\nThank you!\n${placeholders.business_name}`));
    setEmailCc(tpl.default_cc || '');
    setEmailBcc(tpl.default_bcc || '');
  };

  const confirmSendEmail = async () => {
    setShowEmailConfirm(false);
    setSendingEmail(true);
    
    // Small delay to ensure UI updates before API call
    await new Promise(resolve => setTimeout(resolve, 200));

    try {
      await axios.post(`${API}/jobs/${jobId}/email-invoice`, {
        subject: emailSubject,
        body: emailBody,
        cc: emailCc,
        bcc: emailBcc,
        template_key: invoiceTemplateKey,
      });
      toast.success(`✅ Invoice emailed successfully to ${job.email}!`, { duration: 4000 });
      await fetchJob();
    } catch (error) {
      console.error('Error emailing invoice:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send email';
      toast.error(`❌ ${errorMsg}`, { duration: 5000 });
    } finally {
      setSendingEmail(false);
    }
  };

  const cancelSendEmail = () => {
    setShowEmailConfirm(false);
  };

  // Feb 2026 — Review request confirm flow. Preview endpoint returns the
  // rendered subject/body, we show it in an AlertDialog, then POST the send.
  const handleSendReviewRequest = async () => {
    if (!job.email) {
      toast.error('Customer email not found on this job');
      return;
    }
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/review-request/preview`);
      if (!res.data.review_url_configured) {
        toast.error('Google review URL not configured — set it in Settings → Email Templates.', { duration: 5000 });
        return;
      }
      if (res.data.already_sent) {
        const when = res.data.sent_at ? new Date(res.data.sent_at).toLocaleString('en-AU') : 'earlier';
        if (!window.confirm(`Review request was already sent to this customer on ${when}. Send another?`)) return;
      }
      setReviewPreview(res.data);
      setShowReviewConfirm(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare review request');
    }
  };

  const confirmSendReviewRequest = async () => {
    setShowReviewConfirm(false);
    setSendingReview(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/email-review-request`, {
        subject: reviewPreview?.subject,
        body:    reviewPreview?.body,
        cc:      reviewPreview?.cc || '',
        bcc:     reviewPreview?.bcc || '',
      });
      toast.success(`⭐ Review request sent to ${job.email}`);
      await fetchJob();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send review request';
      toast.error(`❌ ${errorMsg}`, { duration: 5000 });
    } finally {
      setSendingReview(false);
      setReviewPreview(null);
    }
  };

  // Resend a specific email from the history — reuses the exact stored body.
  // `storageIndex` is the position in the original invoice_emails_sent array
  // (NOT the reversed display index used by the map below).
  const resendEmail = async (storageIndex, log) => {
    if (resendingEmailIndex !== null) return; // already sending
    const to = log?.sent_to || job?.email || 'the customer';
    if (!window.confirm(`Resend this exact email to ${to}?\n\nSubject: ${log?.subject || '(no subject)'}\n\nThis will send the same HTML body again and add a new entry to the email history.`)) {
      return;
    }
    setResendingEmailIndex(storageIndex);
    try {
      const r = await axios.post(`${API}/jobs/${jobId}/resend-email/${storageIndex}`);
      toast.success(`📧 Email resent to ${to}`, { duration: 4000 });
      if (r?.data?.sent_at) {
        console.log('Resent at', r.data.sent_at);
      }
      await fetchJob();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to resend email';
      toast.error(`❌ ${msg}`, { duration: 6000 });
    } finally {
      setResendingEmailIndex(null);
    }
  };

  if (!job || !settings) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="job-detail-container">
      <Card>
        <CardHeader>
          <div className="job-detail-header">
            <div>
              <CardTitle>Job #{job.job_number}</CardTitle>
              <CardDescription>{job.customer_name}</CardDescription>
            </div>
            <div className="job-detail-actions">
              {!editing ? (
                <>
                  <Button onClick={() => {
                    // Enter edit mode. Seed formData so the "Total Amount" input reflects the
                    // RRP (pre-discount) — either the stored rrp_total_amount (new jobs) or
                    // the current total_amount (legacy jobs that predate the discount feature).
                    const rrp = (job.rrp_total_amount && job.rrp_total_amount > 0)
                      ? job.rrp_total_amount
                      : (job.total_amount || 0);
                    setFormData({
                      ...job,
                      total_amount: rrp,
                      discount_percent: job.discount_percent || 0,
                    });
                    setEditing(true);
                  }} data-testid="edit-job-button">Edit Job</Button>
                  <Button
                    onClick={printSticker}
                    data-testid="print-sticker-button"
                    variant="outline"
                    title="Reprint job sticker"
                  >
                    🏷️ Print Sticker
                  </Button>
                  <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        onClick={handleOpenEmailComposer} 
                        data-testid="email-customer-button"
                        style={{ backgroundColor: '#10b981', color: 'white' }}
                      >
                        📧 Email Customer
                      </Button>
                    </DialogTrigger>
                    <DialogContent style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}>
                      <DialogHeader>
                        <DialogTitle>Email Customer</DialogTitle>
                        <DialogDescription>Compose and send email directly to {job.customer_name}</DialogDescription>
                      </DialogHeader>
                      <div style={{ display: 'grid', gap: '1rem', padding: '1rem 0' }}>
                        <div style={{ 
                          padding: '0.75rem', 
                          backgroundColor: '#f0f9ff', 
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem'
                        }}>
                          <div><strong>To:</strong> {job.email}</div>
                          <div><strong>Customer:</strong> {job.customer_name}</div>
                          <div><strong>Job:</strong> #{job.job_number}</div>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Template
                          </label>
                          <select
                            value={selectedEmailTemplateId}
                            onChange={(e) => handleSelectEmailTemplate(e.target.value)}
                            disabled={sendingJobEmail}
                            data-testid="email-template-select"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              backgroundColor: 'white',
                            }}
                          >
                            <option value="">-- Custom (write your own) --</option>
                            {(settings?.email_templates?.customer_templates || []).map((tpl) => (
                              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                            ))}
                          </select>
                          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Pick a template to prefill subject &amp; message. You can still edit before sending. Manage templates in Settings → Email Templates.
                          </div>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Subject *
                          </label>
                          <Input
                            value={emailComposer.subject}
                            onChange={(e) => setEmailComposer({ ...emailComposer, subject: e.target.value })}
                            placeholder="Email subject"
                            disabled={sendingJobEmail}
                          />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.85rem' }}>
                              CC <span style={{ fontWeight: 400, color: '#94a3b8' }}>(comma-separated)</span>
                            </label>
                            <Input
                              value={emailComposer.cc}
                              onChange={(e) => setEmailComposer({ ...emailComposer, cc: e.target.value })}
                              placeholder="you@yourbusiness.com"
                              disabled={sendingJobEmail}
                              data-testid="email-customer-cc-input"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.85rem' }}>
                              BCC <span style={{ fontWeight: 400, color: '#94a3b8' }}>(hidden)</span>
                            </label>
                            <Input
                              value={emailComposer.bcc}
                              onChange={(e) => setEmailComposer({ ...emailComposer, bcc: e.target.value })}
                              placeholder="accountant@example.com"
                              disabled={sendingJobEmail}
                              data-testid="email-customer-bcc-input"
                            />
                          </div>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Message *
                          </label>
                          <textarea
                            value={emailComposer.body}
                            onChange={(e) => setEmailComposer({ ...emailComposer, body: e.target.value })}
                            placeholder="Type your message here..."
                            rows={12}
                            disabled={sendingJobEmail}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                          />
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            You can edit the message before sending
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setEmailDialogOpen(false)}
                          disabled={sendingJobEmail}
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleSendJobEmail}
                          disabled={sendingJobEmail}
                          style={{ 
                            backgroundColor: sendingJobEmail ? '#f59e0b' : '#2563eb',
                            color: 'white',
                            cursor: sendingJobEmail ? 'wait' : 'pointer'
                          }}
                          data-testid="send-email-button"
                        >
                          {sendingJobEmail ? '⏳ Sending...' : '📧 Send Email'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  {/* SMS Button */}
                  {settings?.twilio_enabled && job.mobile && (
                    <Dialog open={smsModalOpen} onOpenChange={setSmsModalOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          data-testid="sms-customer-button"
                          style={{ backgroundColor: '#8b5cf6', color: 'white' }}
                        >
                          📱 Send SMS
                        </Button>
                      </DialogTrigger>
                      <DialogContent style={{ maxWidth: '500px' }}>
                        <DialogHeader>
                          <DialogTitle>Send SMS to {job.customer_name}</DialogTitle>
                          <DialogDescription>Mobile: {job.mobile}</DialogDescription>
                        </DialogHeader>
                        <div style={{ display: 'grid', gap: '1rem', padding: '1rem 0' }}>
                          {/* SMS Templates */}
                          {settings?.sms_templates && settings.sms_templates.length > 0 && (
                            <div>
                              <Label style={{ marginBottom: '0.5rem', display: 'block' }}>Quick Templates:</Label>
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                {settings.sms_templates.map((template, idx) => (
                                  <Button
                                    key={idx}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => applySmsTemplate(template)}
                                  >
                                    {template.name}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div>
                            <Label htmlFor="sms-message">Message</Label>
                            <Textarea
                              id="sms-message"
                              value={smsMessage}
                              onChange={(e) => setSmsMessage(e.target.value)}
                              placeholder="Type your SMS message..."
                              rows={4}
                              style={{ marginTop: '0.5rem' }}
                              data-testid="sms-message-input"
                            />
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              {smsMessage.length}/160 characters {smsMessage.length > 160 && `(${Math.ceil(smsMessage.length / 160)} SMS)`}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <Button variant="outline" onClick={() => setSmsModalOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={sendSms} 
                            disabled={sendingSms || !smsMessage.trim()}
                            style={{ backgroundColor: '#8b5cf6', color: 'white' }}
                            data-testid="send-sms-btn"
                          >
                            {sendingSms ? 'Sending...' : '📱 Send SMS'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {/* Internal Notes Button & Dialog */}
                  <Dialog open={internalNotesOpen} onOpenChange={setInternalNotesOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        data-testid="internal-notes-btn"
                        style={{ position: 'relative' }}
                      >
                        🔒 Internal Notes
                        {(() => {
                          const noteCount = Array.isArray(job.internal_notes) ? job.internal_notes.length : 0;
                          const hasImaging = !!(job.imaging_location && job.imaging_location.trim());
                          if (noteCount === 0 && !hasImaging) return null;
                          return (
                            <span
                              data-testid="internal-notes-indicator"
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                display: 'flex',
                                gap: '3px',
                                alignItems: 'center',
                              }}
                            >
                              {noteCount > 0 && (
                                <span
                                  data-testid="internal-notes-badge-notes"
                                  title={`${noteCount} note${noteCount === 1 ? '' : 's'} saved`}
                                  style={{
                                    minWidth: '20px',
                                    height: '20px',
                                    padding: '0 5px',
                                    borderRadius: '9999px',
                                    backgroundColor: '#10b981',
                                    border: '2px solid white',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    fontWeight: '700',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: 1,
                                    gap: '2px',
                                  }}
                                >
                                  <span style={{ fontSize: '0.65rem' }}>📝</span>
                                  {noteCount}
                                </span>
                              )}
                              {hasImaging && (
                                <span
                                  data-testid="internal-notes-badge-imaging"
                                  title={`Imaging location: ${job.imaging_location}`}
                                  style={{
                                    minWidth: '20px',
                                    height: '20px',
                                    padding: '0 5px',
                                    borderRadius: '9999px',
                                    backgroundColor: '#3b82f6',
                                    border: '2px solid white',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    fontWeight: '700',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: 1,
                                  }}
                                >
                                  📍
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </Button>
                    </DialogTrigger>
                    <DialogContent style={{ maxWidth: '600px', maxHeight: '85vh', overflow: 'auto' }}>
                      <DialogHeader>
                        <DialogTitle>Internal Notes - Job #{job.job_number}</DialogTitle>
                        <DialogDescription>Private notes and profit summary (not visible to customers)</DialogDescription>
                      </DialogHeader>
                      <div style={{ display: 'grid', gap: '1.5rem', paddingTop: '1rem' }}>
                        {/* Profit Summary */}
                        <div style={{ 
                          padding: '1rem', 
                          backgroundColor: '#f0fdf4', 
                          borderRadius: '8px',
                          border: '1px solid #86efac'
                        }}>
                          <h4 style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#166534' }}>📊 Job Profit Summary</h4>
                          {(() => {
                            const profit = calculateJobProfit();
                            return (
                              <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>Invoice Amount:</span>
                                  <span style={{ fontWeight: '500' }}>${profit.revenue.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>Parts/Materials Cost:</span>
                                  <span style={{ fontWeight: '500', color: '#dc2626' }}>-${profit.costs.toFixed(2)}</span>
                                </div>
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  paddingTop: '0.5rem',
                                  borderTop: '1px solid #86efac',
                                  fontWeight: '600'
                                }}>
                                  <span>Job Profit:</span>
                                  <span style={{ color: profit.profit >= 0 ? '#166534' : '#dc2626' }}>
                                    ${profit.profit.toFixed(2)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: '#6b7280' }}>
                                  <span>Profit Margin:</span>
                                  <span>{profit.margin.toFixed(1)}%</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Imaging Location */}
                        <div>
                          <Label htmlFor="imaging_location" style={{ fontWeight: '600' }}>📍 Imaging Location</Label>
                          <Input
                            id="imaging_location"
                            placeholder="e.g., Shelf A3, Cabinet 2, etc."
                            value={internalNotesForm.imaging_location}
                            onChange={(e) => setInternalNotesForm({ ...internalNotesForm, imaging_location: e.target.value })}
                            style={{ marginTop: '0.5rem' }}
                          />
                        </div>

                        {/* Internal Notes — Add new + history */}
                        <div>
                          <Label htmlFor="internal_notes" style={{ fontWeight: '600' }}>📝 Add a new internal note</Label>
                          <Textarea
                            id="internal_notes"
                            placeholder="Type a note and click Save... (e.g. customer called regarding delivery)"
                            value={internalNotesForm.internal_notes}
                            onChange={(e) => setInternalNotesForm({ ...internalNotesForm, internal_notes: e.target.value })}
                            style={{ marginTop: '0.5rem', minHeight: '100px' }}
                          />
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            Each note is timestamped and saved separately. Never shown on invoices or customer communications.
                          </p>
                        </div>

                        {/* Save Button */}
                        <Button 
                          onClick={saveInternalNotes} 
                          disabled={savingInternalNotes}
                          data-testid="save-internal-notes-btn"
                        >
                          {savingInternalNotes ? 'Saving...' : '💾 Save Note'}
                        </Button>

                        {/* Notes History */}
                        {Array.isArray(job.internal_notes) && job.internal_notes.length > 0 && (
                          <div style={{
                            padding: '1rem',
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.5rem',
                          }}>
                            <h4 style={{
                              fontSize: '1rem',
                              fontWeight: '600',
                              marginBottom: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: '#1e40af',
                            }}>
                              <span>📒</span>
                              <span>Notes History</span>
                              <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500' }}>
                                ({job.internal_notes.length})
                              </span>
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {[...job.internal_notes].reverse().map((note) => (
                                <div
                                  key={note.id}
                                  style={{
                                    padding: '0.75rem',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0.375rem',
                                  }}
                                  data-testid={`internal-note-${note.id}`}
                                >
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: '0.5rem',
                                    marginBottom: '0.25rem',
                                  }}>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                      <span style={{ fontWeight: '600', color: '#0f172a' }}>{note.created_by || 'admin'}</span>
                                      {' • '}
                                      {note.created_at
                                        ? new Date(note.created_at).toLocaleString('en-AU', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true,
                                          })
                                        : ''}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => deleteInternalNote(note.id)}
                                      title="Delete this note"
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#dc2626',
                                        fontSize: '0.875rem',
                                        padding: '0 4px',
                                      }}
                                      data-testid={`delete-internal-note-${note.id}`}
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                  <div style={{
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '0.875rem',
                                    color: '#0f172a',
                                    lineHeight: '1.4',
                                  }}>
                                    {note.text}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Send to iPad Kiosk Button */}
                  <Button 
                    onClick={sendToKiosk}
                    disabled={sendingToKiosk}
                    style={{ 
                      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', 
                      color: 'white' 
                    }}
                    data-testid="send-to-ipad-btn"
                  >
                    {sendingToKiosk ? '⏳ Sending...' : '📱 Send to iPad'}
                  </Button>
                  
                  {/* Kiosk Signature Status */}
                  {job.kiosk_signature && (
                    <span style={{ 
                      padding: '0.25rem 0.75rem', 
                      background: '#dcfce7', 
                      color: '#166534',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      ✅ Signed on iPad
                    </span>
                  )}
                  
                  <Button variant="outline" onClick={() => { if (window.history.length > 1) { navigate(-1); } else { navigate('/'); } }} data-testid="back-to-dashboard-button">Back</Button>
                </>
              ) : (
                <>
                  <Button onClick={handleUpdate} data-testid="save-changes-button">Save Changes</Button>
                  <Button variant="outline" onClick={() => { setEditing(false); setFormData(job); }} data-testid="cancel-edit-button">Cancel</Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="job-detail-content">
            {editing ? (
              <div className="job-form">
                <div className="form-section">
                  <h3>Customer Information</h3>
                  <div className="form-grid">
                    <div className="form-field">
                      <Label>Customer Name</Label>
                      <Input value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <Label>Company Name</Label>
                      <Input value={formData.company_name || ''} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Business customer's company (optional)" data-testid="edit-company-name-input" />
                    </div>
                    <div className="form-field">
                      <Label>ABN</Label>
                      <Input value={formData.abn || ''} onChange={(e) => setFormData({...formData, abn: e.target.value})} placeholder="Australian Business Number (optional)" data-testid="edit-abn-input" />
                    </div>
                    <div className="form-field">
                      <Label>Customer Reference</Label>
                      <Input value={formData.customer_reference || ''} onChange={(e) => setFormData({...formData, customer_reference: e.target.value})} placeholder="Customer's own reference" />
                    </div>
                    <div className="form-field">
                      <Label>Mobile</Label>
                      <Input value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} />
                    </div>
                    <div className="form-field full-width">
                      <Label>Email</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                    </div>
                    <div className="form-field full-width">
                      <Label>Address Line 1</Label>
                      <Input value={formData.address_line1} onChange={(e) => setFormData({...formData, address_line1: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <Label>Suburb</Label>
                      <Input value={formData.suburb} onChange={(e) => setFormData({...formData, suburb: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <Label>Postcode</Label>
                      <Input value={formData.postcode} onChange={(e) => setFormData({...formData, postcode: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <Label>State</Label>
                      <Select value={formData.state} onValueChange={(value) => setFormData({...formData, state: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(settings.states || ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT']).map((state) => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Job Details</h3>
                  <div className="form-grid">
                    <div className="form-field">
                      <Label style={{ display: 'flex', alignItems: 'center' }}>
                        Status
                        <HelpTooltip 
                          content="Booked: Received | Quoted: Quote sent | Wait: Awaiting approval | Approved: Work can begin | Parts: Waiting for parts | Finish: Completed | Not Proceeding: Declined" 
                          side="right"
                        />
                      </Label>
                      <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {optionsWithFallback(settings.statuses, formData.status).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.value}{opt.legacy ? ' (legacy)' : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field">
                      <Label>Priority</Label>
                      <Select value={formData.priority || 'Standard'} onValueChange={(value) => setFormData({...formData, priority: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          {optionsWithFallback(settings.priorities || ['Standard', 'Urgent', 'Special'], formData.priority).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.value}{opt.legacy ? ' (legacy)' : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                      <input
                        type="checkbox"
                        id="closed-checkbox"
                        checked={formData.closed || false}
                        onChange={(e) => setFormData({...formData, closed: e.target.checked})}
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      <Label htmlFor="closed-checkbox" style={{ cursor: 'pointer', margin: 0 }}>Closed</Label>
                    </div>
                    <div className="form-field">
                      <Label>Job Type</Label>
                      <Select value={formData.job_type} onValueChange={(value) => setFormData({...formData, job_type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {optionsWithFallback(settings.job_types, formData.job_type).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.value}{opt.legacy ? ' (legacy)' : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field">
                      <Label>Brand</Label>
                      <Select value={formData.brand} onValueChange={(value) => setFormData({...formData, brand: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {optionsWithFallback(settings.brands, formData.brand).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.value}{opt.legacy ? ' (legacy)' : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field">
                      <Label>How did you find us?</Label>
                      <Select value={formData.referral_source || ''} onValueChange={(value) => setFormData({...formData, referral_source: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          {optionsWithFallback(settings.referral_sources || ['Google', 'Referral', 'Partner', 'Walk-in'], formData.referral_source).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.value}{opt.legacy ? ' (legacy)' : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field">
                      <Label>Referred By <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#6b7280' }}>(partner)</span></Label>
                      <ReferredByAutocomplete
                        value={{ id: formData.referred_by_partner_id, name: formData.referred_by_partner_name }}
                        onChange={({ id, name }) => setFormData({
                          ...formData,
                          referred_by_partner_id: id || null,
                          referred_by_partner_name: name || '',
                          // Auto-align channel when a partner is added but not when cleared
                          referral_source: id ? 'Partner' : formData.referral_source,
                        })}
                        placeholder="Start typing e.g. DRC…"
                        testId="job-detail-referred-by-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label>Model Number</Label>
                      <Input value={formData.model_number} onChange={(e) => setFormData({...formData, model_number: e.target.value})} onKeyDown={advanceOnEnter} data-testid="job-detail-model-number-input" />
                    </div>
                    <div className="form-field">
                      <Label>Serial Number</Label>
                      <Input value={formData.serial_number} onChange={(e) => setFormData({...formData, serial_number: e.target.value})} onKeyDown={advanceOnEnter} data-testid="job-detail-serial-number-input" />
                    </div>
                    <div className="form-field">
                      <Label>Accessories</Label>
                      <AccessoryPicker
                        value={Array.isArray(formData.accessories) ? formData.accessories : []}
                        onChange={(list) => setFormData({...formData, accessories: list})}
                        options={settings?.accessory_options || []}
                      />
                    </div>
                    <div className="form-field">
                      <Label>Date In</Label>
                      <Input type="date" value={formData.date_in} onChange={(e) => setFormData({...formData, date_in: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <Label>Date Out</Label>
                      <Input type="date" value={formData.date_out || ''} onChange={(e) => setFormData({...formData, date_out: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <Label>Date Finished</Label>
                      <Input type="date" value={formData.date_finished || ''} onChange={(e) => setFormData({...formData, date_finished: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <Label>Date Due</Label>
                      <Input type="date" value={formData.date_due || ''} onChange={(e) => setFormData({...formData, date_due: e.target.value})} />
                    </div>
                    <div className="form-field full-width">
                      <Label>Job Description</Label>
                      <Textarea value={formData.job_description} onChange={(e) => setFormData({...formData, job_description: e.target.value})} rows={3} />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Financial Information</h3>
                  <div className="form-grid">
                    <div className="form-field">
                      <Label>
                        Total Amount (inc. GST)
                        {parseFloat(formData.discount_percent) > 0 && (
                          <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 400, color: '#6b7280' }}>
                            (RRP — pre-discount)
                          </span>
                        )}
                      </Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        min="0"
                        value={formData.total_amount || 0} 
                        onChange={(e) => {
                          // In edit mode, total_amount input == RRP; net is derived on save.
                          const rrp = parseFloat(e.target.value) || 0;
                          const gstPercent = parseFloat(formData.gst_percentage) || 10;
                          const discPct = Math.max(0, Math.min(100, parseFloat(formData.discount_percent) || 0));
                          const discAmt = parseFloat((rrp * (discPct / 100)).toFixed(2));
                          const netTotal = rrp - discAmt;
                          const subtotal = netTotal / (1 + gstPercent / 100);
                          const gstAmount = netTotal - subtotal;
                          setFormData({
                            ...formData, 
                            total_amount: rrp,
                            discount_amount: discAmt,
                            subtotal: parseFloat(subtotal.toFixed(2)),
                            gst_amount: parseFloat(gstAmount.toFixed(2))
                          });
                        }} 
                        data-testid="detail-total-amount-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label>
                        Partner Discount %
                        <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 400, color: '#6b7280' }}>(optional)</span>
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.discount_percent || 0}
                        onChange={(e) => {
                          const discPct = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                          const rrp = parseFloat(formData.total_amount) || 0;
                          const gstPercent = parseFloat(formData.gst_percentage) || 10;
                          const discAmt = parseFloat((rrp * (discPct / 100)).toFixed(2));
                          const netTotal = rrp - discAmt;
                          const subtotal = netTotal / (1 + gstPercent / 100);
                          const gstAmount = netTotal - subtotal;
                          setFormData({
                            ...formData,
                            discount_percent: discPct,
                            discount_amount: discAmt,
                            subtotal: parseFloat(subtotal.toFixed(2)),
                            gst_amount: parseFloat(gstAmount.toFixed(2)),
                          });
                        }}
                        placeholder="0"
                        data-testid="detail-discount-percent-input"
                      />
                    </div>
                    <div className="form-field">
                      <Label>GST %</Label>
                      <Input 
                        type="number" 
                        step="0.01" 
                        min="0" 
                        max="100"
                        value={formData.gst_percentage || 10} 
                        onChange={(e) => {
                          const gstPercent = parseFloat(e.target.value) || 10;
                          const rrp = parseFloat(formData.total_amount) || 0;
                          const discPct = Math.max(0, Math.min(100, parseFloat(formData.discount_percent) || 0));
                          const discAmt = parseFloat((rrp * (discPct / 100)).toFixed(2));
                          const netTotal = rrp - discAmt;
                          const subtotal = netTotal / (1 + gstPercent / 100);
                          const gstAmount = netTotal - subtotal;
                          setFormData({
                            ...formData, 
                            gst_percentage: gstPercent,
                            subtotal: parseFloat(subtotal.toFixed(2)),
                            gst_amount: parseFloat(gstAmount.toFixed(2))
                          });
                        }} 
                      />
                    </div>
                    <div className="form-field">
                      <Label>Subtotal (ex. GST)</Label>
                      <Input 
                        type="number" 
                        value={formData.subtotal || 0} 
                        readOnly 
                        disabled 
                        className="bg-gray-100"
                      />
                    </div>
                    <div className="form-field">
                      <Label>GST Amount</Label>
                      <Input 
                        type="number" 
                        value={formData.gst_amount || 0} 
                        readOnly 
                        disabled 
                        className="bg-gray-100"
                      />
                    </div>
                    {/* Live discount breakdown */}
                    {parseFloat(formData.discount_percent) > 0 && parseFloat(formData.total_amount) > 0 && (
                      <div
                        className="form-field full-width"
                        data-testid="detail-discount-breakdown"
                        style={{
                          padding: '0.85rem 1rem',
                          backgroundColor: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          gridColumn: '1 / -1',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0' }}>
                          <span style={{ color: '#1e3a8a' }}>RRP Total (inc. GST):</span>
                          <span style={{ fontFamily: 'monospace' }}>${(parseFloat(formData.total_amount) || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0', color: '#b91c1c' }}>
                          <span>Partner Discount ({(parseFloat(formData.discount_percent) || 0).toFixed(2)}%):</span>
                          <span style={{ fontFamily: 'monospace' }}>-${(parseFloat(formData.discount_amount) || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0 0.15rem', marginTop: '0.3rem', borderTop: '1px solid #bfdbfe', fontWeight: 700, color: '#065f46' }}>
                          <span>Net Total (inc. GST):</span>
                          <span style={{ fontFamily: 'monospace' }}>
                            ${((parseFloat(formData.total_amount) || 0) - (parseFloat(formData.discount_amount) || 0)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="job-details-view">
                <div className="detail-section">
                  <h3>Customer Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Customer Name:</span>
                      <span className="detail-value">{job.customer_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Company Name:</span>
                      <span className="detail-value" data-testid="detail-company-name">{job.company_name || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">ABN:</span>
                      <span className="detail-value" data-testid="detail-abn">{job.abn || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Customer Reference:</span>
                      <span className="detail-value">{job.customer_reference || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Mobile:</span>
                      <span className="detail-value">{job.mobile}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{job.email}</span>
                    </div>
                    <div className="detail-item full-width">
                      <span className="detail-label">Address Line 1:</span>
                      <span className="detail-value">{job.address_line1}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Suburb:</span>
                      <span className="detail-value">{job.suburb}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Postcode:</span>
                      <span className="detail-value">{job.postcode}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">State:</span>
                      <span className="detail-value">{job.state}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Job Details
                    {hasQuickChanges && (
                      <Button 
                        onClick={saveQuickChanges}
                        disabled={savingQuickEdit}
                        size="sm"
                        style={{ backgroundColor: '#16a34a', color: 'white' }}
                        data-testid="save-quick-changes-btn"
                      >
                        {savingQuickEdit ? 'Saving...' : '💾 Save Changes'}
                      </Button>
                    )}
                  </h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <Select 
                        value={quickStatus} 
                        onValueChange={handleQuickStatusChange}
                      >
                        <SelectTrigger 
                          style={{ 
                            width: 'auto',
                            minWidth: '140px',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            backgroundColor: settings?.status_colors?.[quickStatus] || '#6b7280',
                            color: '#fff',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          data-testid="quick-status-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['Booked', 'Quoted', 'Wait', 'Approved', 'Finish', 'Parts', 'Not Recoverable', 'Not Proceeding'].map((status) => (
                            <SelectItem key={status} value={status}>
                              <span style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '0.5rem' 
                              }}>
                                <span style={{
                                  width: '10px',
                                  height: '10px',
                                  borderRadius: '50%',
                                  backgroundColor: settings?.status_colors?.[status] || '#6b7280'
                                }}></span>
                                {status}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Progress:</span>
                      <Select
                        value={quickProgressStatus}
                        onValueChange={handleQuickProgressStatusChange}
                      >
                        <SelectTrigger
                          style={{
                            width: 'auto',
                            minWidth: '140px',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            backgroundColor:
                              quickProgressStatus === 'Imaging' ? '#e0f2fe' :
                              quickProgressStatus === 'WIP' ? '#fef3c7' :
                              quickProgressStatus === 'Awaiting Parts' ? '#fee2e2' :
                              '#f8fafc',
                            border: `1px solid ${
                              quickProgressStatus === 'Imaging' ? '#7dd3fc' :
                              quickProgressStatus === 'WIP' ? '#fde047' :
                              quickProgressStatus === 'Awaiting Parts' ? '#fca5a5' :
                              '#e2e8f0'
                            }`,
                            color:
                              quickProgressStatus === 'Imaging' ? '#0369a1' :
                              quickProgressStatus === 'WIP' ? '#a16207' :
                              quickProgressStatus === 'Awaiting Parts' ? '#b91c1c' :
                              '#475569',
                            cursor: 'pointer'
                          }}
                          data-testid="quick-progress-status-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['Not Started', 'Imaging', 'WIP', 'Awaiting Parts'].map((ps) => (
                            <SelectItem key={ps} value={ps}>{ps}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Priority:</span>
                      <Select 
                        value={quickPriority} 
                        onValueChange={handleQuickPriorityChange}
                      >
                        <SelectTrigger 
                          style={{ 
                            width: 'auto',
                            minWidth: '120px',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            backgroundColor: quickPriority === 'Urgent' ? '#fef2f2' : quickPriority === 'Special' ? '#fefce8' : '#f8fafc',
                            border: `1px solid ${quickPriority === 'Urgent' ? '#fca5a5' : quickPriority === 'Special' ? '#fde047' : '#e2e8f0'}`,
                            color: quickPriority === 'Urgent' ? '#dc2626' : quickPriority === 'Special' ? '#ca8a04' : '#374151',
                            cursor: 'pointer'
                          }}
                          data-testid="quick-priority-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(settings?.priorities || ['Standard', 'Urgent', 'Special']).map((priority) => (
                            <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Job Type:</span>
                      <span className="detail-value">{job.job_type}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Brand:</span>
                      <span className="detail-value">{job.brand}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">How did you find us?:</span>
                      <span className="detail-value">{job.referral_source || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Referred By:</span>
                      <span className="detail-value" data-testid="job-detail-referred-by">
                        {job.referred_by_partner_name ? (
                          <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: '0.8rem', fontWeight: 600 }}>
                            {job.referred_by_partner_name}
                          </span>
                        ) : 'N/A'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Model Number:</span>
                      <span className="detail-value">{job.model_number || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Serial Number:</span>
                      <span className="detail-value">{job.serial_number || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Accessories:</span>
                      <span className="detail-value">{Array.isArray(job.accessories) ? (job.accessories.length > 0 ? job.accessories.join(', ') : 'N/A') : (job.accessories || 'N/A')}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Date In:</span>
                      <span className="detail-value">{formatDate(job.date_in)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Date Out:</span>
                      <span className="detail-value">{formatDate(job.date_out)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Date Finished:</span>
                      <span className="detail-value">{formatDate(job.date_finished)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Date Due:</span>
                      <button
                        type="button"
                        onClick={openInlineDateDueDialog}
                        title="Click to set / change Date Due (no Edit needed)"
                        data-testid="date-due-inline-edit"
                        style={{
                          background: 'transparent',
                          border: '1px dashed transparent',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          margin: '-2px -6px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: 'inherit',
                          color: job.date_due ? 'inherit' : '#9ca3af',
                          fontStyle: job.date_due ? 'normal' : 'italic',
                          transition: 'background-color 120ms, border-color 120ms',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#eff6ff';
                          e.currentTarget.style.borderColor = '#93c5fd';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        {job.date_due ? formatDate(job.date_due) : 'Set date…'} <span style={{ opacity: 0.55, marginLeft: 4 }}>✏️</span>
                      </button>
                    </div>
                    <div className="detail-item full-width">
                      <span className="detail-label">Job Description:</span>
                      <span className="detail-value">{job.job_description || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Issue Summary & Quote Section */}
                <div className="detail-section" style={{ marginTop: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1e40af', marginBottom: '1rem' }}>
                    📋 Issue Summary & Quote
                    <HelpTooltip 
                      content="Create quotes with issue descriptions and pricing. Email quotes to customers for online approval. Track quote versions and customer responses." 
                      side="right"
                    />
                  </h3>
                  
                  {/* Latest Quote Display (if exists) */}
                  {job.quote_history && job.quote_history.length > 0 && (
                    <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem', border: '1px solid #bfdbfe' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          <strong>Current Quote:</strong> (Version {job.quote_history[job.quote_history.length - 1].version})
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {job.quote_history[job.quote_history.length - 1].sent_to_customer && (
                            <span style={{ fontSize: '0.75rem', color: '#059669', backgroundColor: '#d1fae5', padding: '0.125rem 0.5rem', borderRadius: '0.25rem' }}>
                              ✓ Sent to customer
                            </span>
                          )}
                          {job.quote_history[job.quote_history.length - 1].approval_status === 'accepted' && (
                            <span style={{ fontSize: '0.75rem', color: '#fff', backgroundColor: '#059669', padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontWeight: '600' }}>
                              ✓ ACCEPTED
                            </span>
                          )}
                          {/* Deposit-received tick (Feb 2026) — visible once the
                              job has moved past the quoting stage. Two paths
                              qualify: (a) customer accepted the remote quote
                              (approval_status==='accepted') OR (b) operator
                              flipped the job status to "Approved" manually
                              after a verbal approval. Purely a workflow
                              milestone: flips a flag + auto-comment; NO
                              payment record is created. */}
                          {(job.quote_history[job.quote_history.length - 1].approval_status === 'accepted' || job.status === 'Approved') && (
                            job.deposit_received ? (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm('Remove the "Deposit Received" flag? A reversal note will be added to the timeline.')) return;
                                  try {
                                    const r = await axios.post(`${API}/jobs/${jobId}/deposit-received/toggle`);
                                    setJob(r.data); setFormData(r.data);
                                    toast.success('Deposit-received flag cleared');
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to update');
                                  }
                                }}
                                title={`Deposit received ${job.deposit_received_at ? new Date(job.deposit_received_at).toLocaleString('en-AU') : ''}${job.deposit_received_by ? ' by ' + job.deposit_received_by : ''} — click to clear`}
                                style={{ fontSize: '0.75rem', color: '#fff', backgroundColor: '#2563eb', padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontWeight: '600', border: 'none', cursor: 'pointer' }}
                                data-testid="deposit-received-clear-btn"
                              >
                                💵 Deposit Received{job.deposit_received_at ? ` · ${new Date(job.deposit_received_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}` : ''}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    const r = await axios.post(`${API}/jobs/${jobId}/deposit-received/toggle`);
                                    setJob(r.data); setFormData(r.data);
                                    toast.success('💵 Deposit marked as received', { duration: 3000 });
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to update');
                                  }
                                }}
                                title="Click to mark that the customer has paid the deposit / commencement fee. A timestamped comment will be added automatically."
                                style={{ fontSize: '0.75rem', color: '#1e3a8a', backgroundColor: '#eff6ff', padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontWeight: '600', border: '1px dashed #93c5fd', cursor: 'pointer' }}
                                data-testid="deposit-received-mark-btn"
                              >
                                💵 Mark Deposit Received
                              </button>
                            )
                          )}
                          {job.quote_history[job.quote_history.length - 1].approval_status === 'rejected' && (
                            <span style={{ fontSize: '0.75rem', color: '#fff', backgroundColor: '#dc2626', padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontWeight: '600' }}>
                              ✗ DECLINED
                            </span>
                          )}
                          {job.quote_history[job.quote_history.length - 1].approval_status === 'pending' && (
                            <span style={{ fontSize: '0.75rem', color: '#92400e', backgroundColor: '#fef3c7', padding: '0.125rem 0.5rem', borderRadius: '0.25rem' }}>
                              ⏳ Awaiting Response
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem', color: '#374151' }}>
                        {job.quote_history[job.quote_history.length - 1].text}
                      </div>
                      {job.quote_history[job.quote_history.length - 1].rejection_reason && (
                        <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#fee2e2', borderRadius: '0.25rem', border: '1px solid #fecaca' }}>
                          <strong style={{ color: '#dc2626', fontSize: '0.75rem' }}>Rejection Reason: </strong>
                          <span style={{ color: '#991b1b', fontSize: '0.875rem' }}>
                            {job.quote_history[job.quote_history.length - 1].rejection_reason}
                          </span>
                        </div>
                      )}
                      {job.quote_history[job.quote_history.length - 1].amount != null && (
                        <div style={{ marginTop: '0.75rem', padding: '0.5rem', backgroundColor: '#fef3c7', borderRadius: '0.25rem', display: 'inline-block' }}>
                          <strong style={{ color: '#92400e' }}>Quoted Amount: </strong>
                          <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#b45309' }}>
                            ${job.quote_history[job.quote_history.length - 1].amount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                        Updated by {job.quote_history[job.quote_history.length - 1].updated_by} on {formatDateTime(job.quote_history[job.quote_history.length - 1].updated_at)}
                        {job.quote_history[job.quote_history.length - 1].sent_at && (
                          <span> • Emailed on {formatDateTime(job.quote_history[job.quote_history.length - 1].sent_at)}</span>
                        )}
                        {job.quote_history[job.quote_history.length - 1].approval_date && (
                          <span> • {job.quote_history[job.quote_history.length - 1].approval_status === 'accepted' ? 'Accepted' : 'Declined'} on {formatDateTime(job.quote_history[job.quote_history.length - 1].approval_date)}</span>
                        )}
                      </div>
                      {job.quote_history.length > 1 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setShowQuoteHistory(!showQuoteHistory)}
                          style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}
                        >
                          {showQuoteHistory ? '▲ Hide History' : '▼ View History'} ({job.quote_history.length - 1} previous versions)
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Quote History (collapsible) */}
                  {showQuoteHistory && job.quote_history && job.quote_history.length > 1 && (
                    <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem', color: '#6b7280' }}>
                        Quote History
                      </h4>
                      {job.quote_history.slice(0, -1).reverse().map((quote, index) => (
                        <div key={index} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>
                              Version {quote.version} - {formatDateTime(quote.updated_at)} by {quote.updated_by}
                            </div>
                            {quote.sent_to_customer && (
                              <span style={{ fontSize: '0.625rem', color: '#059669', backgroundColor: '#d1fae5', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>
                                ✓ Sent
                              </span>
                            )}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', color: '#6b7280' }}>
                            {quote.text}
                          </div>
                          {quote.amount != null && (
                            <div style={{ fontSize: '0.75rem', color: '#b45309', marginTop: '0.25rem' }}>
                              <strong>Amount:</strong> ${quote.amount.toFixed(2)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Quote Input */}
                  <div>
                    <Label htmlFor="quote-text">
                      {job.quote_history && job.quote_history.length > 0 ? 'Update Quote' : 'Write Quote'}
                    </Label>
                    <Textarea
                      id="quote-text"
                      data-testid="quote-text-input"
                      value={quoteText}
                      onChange={(e) => setQuoteText(e.target.value)}
                      placeholder="Describe the issue discussed with the customer, diagnosis, proposed solution, and estimated cost..."
                      rows={4}
                      style={{ marginTop: '0.5rem' }}
                    />
                    <div style={{ marginTop: '0.75rem' }}>
                      <Label htmlFor="quote-amount">Quote Amount ($)</Label>
                      <Input
                        id="quote-amount"
                        data-testid="quote-amount-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={quoteAmount}
                        onChange={(e) => setQuoteAmount(e.target.value)}
                        placeholder="Enter quote amount (optional)"
                        style={{ marginTop: '0.25rem', maxWidth: '200px' }}
                      />
                      <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.35rem', maxWidth: '480px', lineHeight: 1.4 }}>
                        Leave blank for multi-option quotes (e.g. "$325 recovery + $100 drive OR customer-supplied"). When blank, the customer email and approval page hide the headline price so your description does the talking. Fill it only when there's a single firm number to display.
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                      <Button 
                        onClick={handleSaveQuote} 
                        disabled={savingQuote || !quoteText.trim()}
                        data-testid="save-quote-button"
                        style={{ backgroundColor: '#10b981', color: 'white' }}
                      >
                        {savingQuote ? 'Saving...' : '💾 Save Quote'}
                      </Button>
                      <Button 
                        onClick={handleEmailQuote} 
                        disabled={emailingQuote || !job.quote_history || job.quote_history.length === 0}
                        data-testid="email-quote-button"
                        variant="outline"
                      >
                        {emailingQuote ? 'Sending...' : '📧 Email Quote to Customer'}
                      </Button>
                    </div>
                    {(!job.quote_history || job.quote_history.length === 0) && (
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                        💡 Save a quote first before emailing to customer
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Section Divider: Quote → Work & Billing */}
            {!editing && (
              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '1.5rem 0' }} />
            )}

            {/* Payment Tracking Section */}
            {!editing && (
              <div className="payment-tracking-section">
                {/* Work & Billing Section */}
                <div className="work-billing-section" style={{ 
                  marginTop: '1.5rem',
                  padding: '1.5rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '0.75rem',
                  border: '1px solid #e2e8f0'
                }}>
                  {/* Header row: 💼 Work & Billing on the left, Mark-as-Payment-Received
                      toggle on the right. Same interaction pattern as the Deposit
                      Received button on the Quote card — flips a visual pill, appends
                      a timestamped comment, does NOT create a payment record.
                      Used for the "customer paid but hasn't collected yet" scenario
                      so the operator sees the status without scrolling to Payments. */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1.5rem',
                    gap: '2rem',
                    flexWrap: 'wrap',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.25rem' }}>💼</span>
                      <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Work & Billing</h4>
                    </div>
                    {job.payment_received ? (
                      <button
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/jobs/${jobId}/payment-received/toggle`);
                            setJob(r.data);
                            toast.success('Payment-received flag cleared');
                          } catch {
                            toast.error('Could not update payment-received flag');
                          }
                        }}
                        title={`Payment received ${job.payment_received_at ? new Date(job.payment_received_at).toLocaleString('en-AU') : ''}${job.payment_received_by ? ' by ' + job.payment_received_by : ''} — click to clear`}
                        data-testid="payment-received-clear-btn"
                        style={{
                          padding: '0.55rem 1rem',
                          background: '#10b981',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '999px',
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          boxShadow: '0 1px 2px rgba(16,185,129,0.35)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        💰 Payment Received{job.payment_received_at ? ` · ${new Date(job.payment_received_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}` : ''}
                      </button>
                    ) : (
                      <button
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/jobs/${jobId}/payment-received/toggle`);
                            setJob(r.data);
                            toast.success('Marked as payment received');
                          } catch {
                            toast.error('Could not mark as payment received');
                          }
                        }}
                        title="Click when the customer has paid but hasn't collected the device yet — flips a visual flag, no payment record is created."
                        data-testid="payment-received-mark-btn"
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'transparent',
                          color: '#059669',
                          border: '2px dashed #10b981',
                          borderRadius: '999px',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        💰 Mark as Payment Received
                      </button>
                    )}
                  </div>

                  {/* Service Performed */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <Label htmlFor="work_performed" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                      Service Performed
                    </Label>
                    <Textarea
                      id="work_performed"
                      value={billingForm.work_performed}
                      onChange={(e) => setBillingForm({ ...billingForm, work_performed: e.target.value })}
                      placeholder="Describe the work done (e.g., Data recovery from failed hard drive, replaced controller board...)"
                      rows={3}
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  {/* Labor/Service Charge + Emergency Service — side-by-side.
                      Emergency Service is a discrete inc-GST invoice line item
                      for after-hours / emergency callouts. Leaving it at 0 hides
                      it from the invoice completely — no visual change from the
                      current customer-facing invoice. */}
                  <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <Label htmlFor="labor_amount" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Labor/Service Charge <span style={{ fontWeight: 'normal', color: '#6b7280' }}>(inc GST)</span>
                      </Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.25rem', color: '#374151' }}>$</span>
                        <Input
                          id="labor_amount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={billingForm.labor_amount}
                          onChange={(e) => setBillingForm({ ...billingForm, labor_amount: parseFloat(e.target.value) || 0 })}
                          placeholder="0.00"
                          style={{ maxWidth: '200px' }}
                        />
                      </div>
                    </div>
                    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <Label htmlFor="emergency_service_amount" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Emergency Service <span style={{ fontWeight: 'normal', color: '#6b7280' }}>(inc GST — optional)</span>
                      </Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.25rem', color: '#374151' }}>$</span>
                        <Input
                          id="emergency_service_amount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={billingForm.emergency_service_amount}
                          onChange={(e) => setBillingForm({ ...billingForm, emergency_service_amount: parseFloat(e.target.value) || 0 })}
                          placeholder="0.00"
                          style={{ maxWidth: '200px' }}
                          data-testid="emergency-service-amount-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Partner Discount % — applied to LABOUR only (stock is never discounted).
                      Net Total drives payments/reports; discount_amount + rrp_total_amount preserved for audit + invoice line. */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <Label htmlFor="billing_discount_percent" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                      Partner Discount <span style={{ fontWeight: 'normal', color: '#6b7280' }}>(applied to Labor only — stock is never discounted)</span>
                    </Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Input
                        id="billing_discount_percent"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={billingForm.discount_percent || 0}
                        onChange={(e) => setBillingForm({ ...billingForm, discount_percent: parseFloat(e.target.value) || 0 })}
                        placeholder="0"
                        style={{ maxWidth: '120px' }}
                        data-testid="billing-discount-percent-input"
                      />
                      <span style={{ fontSize: '1.25rem', color: '#374151' }}>%</span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{ borderTop: '1px solid #e2e8f0', margin: '1.5rem 0' }} />

                  {/* Stock Items */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <Label style={{ fontWeight: '600', margin: 0 }}>Stock Items</Label>
                      <Button onClick={handleOpenAddStock} size="sm" variant="outline">
                        + Add Stock
                      </Button>
                    </div>

                    {job.stock_items && job.stock_items.length > 0 ? (
                      <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                        <Table>
                          <TableHeader>
                            <TableRow style={{ backgroundColor: '#f9fafb' }}>
                              <TableHead>Item</TableHead>
                              <TableHead>Qty</TableHead>
                              <TableHead style={{ textAlign: 'right' }}>Price</TableHead>
                              <TableHead style={{ textAlign: 'right' }}>Total</TableHead>
                              <TableHead style={{ width: '80px' }}></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {job.stock_items.map((item, index) => (
                              <TableRow key={item.line_id || `${item.stock_item_id}-${index}`}>
                                <TableCell style={{ fontWeight: '500' }}>
                                  {item.brand ? `${item.brand} ` : ''}{item.stock_item_name}
                                </TableCell>
                                <TableCell>{item.quantity}</TableCell>
                                <TableCell style={{ textAlign: 'right' }}>${item.sell_price.toFixed(2)}</TableCell>
                                <TableCell style={{ textAlign: 'right', fontWeight: '600' }}>${item.total_sell.toFixed(2)}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid={`remove-stock-${item.line_id || item.stock_item_id}`}
                                    onClick={() => handleRemoveStock(item.stock_item_id, item.line_id)}
                                    style={{ color: '#dc2626', padding: '0.25rem 0.5rem' }}
                                  >
                                    Remove
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        backgroundColor: 'white', 
                        borderRadius: '0.5rem',
                        border: '1px dashed #d1d5db',
                        color: '#6b7280'
                      }}>
                        No stock items added yet
                      </div>
                    )}
                  </div>

                  {/* Additional Line Items — mixed jobs (data recovery + computer
                      repair), non-stock parts (2nd hand RAM, etc.), one-off
                      services. Inc-GST. Category tag drives P&L rollup. */}
                  <div style={{ marginTop: '1.5rem' }} data-testid="line-items-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <Label style={{ fontWeight: '600', margin: 0 }}>
                        ➕ Additional Line Items
                        <HelpTooltip
                          content="For mixed jobs (e.g. data recovery + computer repair on the same ticket) and one-off non-stock parts (e.g. 2nd hand RAM). Each row is inc-GST and shows as its own line on the invoice. Category is used by the P&L for reporting."
                          side="right"
                        />
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setBillingForm(prev => ({
                          ...prev,
                          line_items: [
                            ...(prev.line_items || []),
                            { id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, description: '', category: 'Labour', quantity: 1, unit_price: 0, total: 0 },
                          ],
                        }))}
                        data-testid="line-item-add-btn"
                      >
                        + Add line item
                      </Button>
                    </div>
                    {(billingForm.line_items || []).length === 0 ? (
                      <div style={{ padding: '0.75rem', backgroundColor: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '0.5rem', color: '#6b7280', fontSize: '0.85rem', textAlign: 'center' }}>
                        No additional line items. Click &quot;+ Add line item&quot; for mixed jobs or one-off non-stock charges.
                      </div>
                    ) : (
                      <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 130px 70px 110px 100px 40px', gap: '0.5rem', padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                          <div>Description</div>
                          <div>Category</div>
                          <div style={{ textAlign: 'center' }}>Qty</div>
                          <div style={{ textAlign: 'right' }}>Unit $ (inc GST)</div>
                          <div style={{ textAlign: 'right' }}>Total</div>
                          <div></div>
                        </div>
                        {billingForm.line_items.map((li, idx) => {
                          const total = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
                          const updateField = (field, val) => {
                            setBillingForm(prev => {
                              const copy = [...(prev.line_items || [])];
                              copy[idx] = { ...copy[idx], [field]: val };
                              return { ...prev, line_items: copy };
                            });
                          };
                          const remove = () => {
                            setBillingForm(prev => ({ ...prev, line_items: (prev.line_items || []).filter((_, i) => i !== idx) }));
                          };
                          return (
                            <div key={li.id || idx} style={{ display: 'grid', gridTemplateColumns: '2fr 130px 70px 110px 100px 40px', gap: '0.5rem', padding: '0.5rem 0.75rem', alignItems: 'center', borderTop: idx === 0 ? 'none' : '1px solid #f1f5f9' }} data-testid={`line-item-row-${idx}`}>
                              <Input
                                type="text"
                                value={li.description || ''}
                                onChange={(e) => updateField('description', e.target.value)}
                                placeholder="e.g. Computer Repair"
                                data-testid={`line-item-description-${idx}`}
                              />
                              <select
                                value={li.category || 'Labour'}
                                onChange={(e) => updateField('category', e.target.value)}
                                data-testid={`line-item-category-${idx}`}
                                style={{ padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', backgroundColor: 'white', fontSize: '0.875rem', height: '2.25rem' }}
                              >
                                <option value="Labour">Labour</option>
                                <option value="Part">Part</option>
                                <option value="Other">Other</option>
                              </select>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={li.quantity ?? 1}
                                onChange={(e) => updateField('quantity', parseFloat(e.target.value) || 0)}
                                style={{ textAlign: 'center' }}
                                data-testid={`line-item-quantity-${idx}`}
                              />
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={li.unit_price ?? 0}
                                onChange={(e) => updateField('unit_price', parseFloat(e.target.value) || 0)}
                                style={{ textAlign: 'right' }}
                                data-testid={`line-item-unit-price-${idx}`}
                              />
                              <div style={{ textAlign: 'right', fontWeight: 600, color: '#374151' }} data-testid={`line-item-total-${idx}`}>
                                ${total.toFixed(2)}
                              </div>
                              <button
                                type="button"
                                onClick={remove}
                                title="Remove line item"
                                data-testid={`line-item-remove-${idx}`}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#dc2626', padding: '0.25rem' }}
                              >
                                🗑️
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Divider */}
                  <div style={{ borderTop: '1px solid #e2e8f0', margin: '1.5rem 0' }} />

                  {/* Summary */}
                  <div style={{ 
                    backgroundColor: 'white', 
                    borderRadius: '0.5rem', 
                    border: '1px solid #e5e7eb',
                    padding: '1rem'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#374151' }}>Summary</div>
                    {(() => {
                      const totals = calculateBillingTotals();
                      const hasDiscount = totals.discountPercent > 0 && totals.discountAmt > 0;
                      return (
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
                            <span>Labor (inc GST):</span>
                            <span>${totals.laborIncGst.toFixed(2)}</span>
                          </div>
                          {hasDiscount && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b91c1c' }} data-testid="billing-discount-row">
                                <span>Partner Discount ({totals.discountPercent.toFixed(2)}% on labour):</span>
                                <span>-${totals.discountAmt.toFixed(2)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }} data-testid="billing-labor-net-row">
                                <span>Labor after discount:</span>
                                <span>${totals.laborNet.toFixed(2)}</span>
                              </div>
                            </>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
                            <span>Stock (inc GST){hasDiscount ? ' — not discounted' : ''}:</span>
                            <span>${totals.stockTotal.toFixed(2)}</span>
                          </div>
                          {totals.lineItemsTotal > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#374151', fontWeight: 500 }} data-testid="billing-line-items-row">
                              <span>➕ Additional line items (inc GST):</span>
                              <span>${totals.lineItemsTotal.toFixed(2)}</span>
                            </div>
                          )}
                          {totals.emergencyAmount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b45309', fontWeight: 500 }} data-testid="billing-emergency-row">
                              <span>⚡ Emergency Service (inc GST):</span>
                              <span>${totals.emergencyAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div style={{ borderTop: '1px solid #e5e7eb', margin: '0.5rem 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Subtotal (ex GST):</span>
                            <span>${totals.subtotal.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
                            <span>GST ({job?.gst_percentage || 10}%):</span>
                            <span>${totals.gstAmount.toFixed(2)}</span>
                          </div>
                          <div style={{ borderTop: '2px solid #374151', margin: '0.5rem 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.1rem' }}>
                            <span>TOTAL (inc GST):</span>
                            <span style={{ color: '#16a34a' }}>${totals.totalIncGst.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                    <Button 
                      variant="outline"
                      onClick={saveBilling}
                      disabled={savingBilling}
                      data-testid="save-billing-btn"
                    >
                      {savingBilling ? 'Saving...' : 'Save'}
                    </Button>
                    {!job.invoice_generated ? (
                      <Button 
                        onClick={handleQuickGenerateInvoice}
                        disabled={savingBilling}
                        style={{ backgroundColor: '#16a34a' }}
                        data-testid="generate-invoice-btn"
                      >
                        {savingBilling ? 'Generating...' : '📄 Generate Invoice'}
                      </Button>
                    ) : (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button onClick={() => setShowInvoicePreview(!showInvoicePreview)} variant="outline">
                          {showInvoicePreview ? 'Hide' : 'View'} Invoice
                        </Button>
                        <Button
                          onClick={() => {
                            // Smart Reminder — toast which printer to pick
                            if (settings?.printer_invoice) {
                              toast.info(`🖨️ Send invoice to: ${settings.printer_invoice}`, { duration: 4000 });
                            }
                            const printWin = window.open(`${API}/jobs/${jobId}/invoice-view`, '_blank');
                            if (!printWin) {
                              toast.error('Pop-up blocked. Please allow pop-ups for printing.');
                              return;
                            }
                            const triggerPrint = () => {
                              try {
                                printWin.focus();
                                printWin.print();
                              } catch (e) {
                                console.error('Print failed:', e);
                              }
                            };
                            // Wait for the invoice HTML to load before printing
                            printWin.addEventListener('load', triggerPrint);
                            // Fallback in case the load event has already fired
                            setTimeout(() => {
                              if (printWin && !printWin.closed) triggerPrint();
                            }, 1200);
                          }}
                          variant="outline"
                          data-testid="print-invoice-btn"
                        >
                          🖨️ Print
                        </Button>
                        {job.invoice_pdf_path && (
                          <Button 
                            onClick={() => window.open(`${BACKEND_URL}${job.invoice_pdf_path}`, '_blank')}
                            variant="outline"
                          >
                            📄 PDF
                          </Button>
                        )}
                        <Button 
                          onClick={handleEmailInvoice}
                          disabled={sendingEmail}
                          style={{ backgroundColor: '#2563eb', color: 'white' }}
                        >
                          {sendingEmail ? '⏳ Sending...' : '📧 Email'}
                        </Button>
                        {/* Feb 2026 — Send review request. Only makes sense
                            once the job is Paid and the invoice has been
                            emailed. If already sent, shows a subtle
                            "Requested" badge instead of the button. */}
                        {(job.payment_status || '').toLowerCase() === 'paid' && (job.invoice_emails_sent?.length || 0) > 0 && (
                          job.review_request_sent ? (
                            <span
                              data-testid="review-already-sent-badge"
                              title={`Review requested${job.review_request_sent_at ? ` on ${new Date(job.review_request_sent_at).toLocaleString('en-AU')}` : ''}${job.review_request_sent_by ? ` by ${job.review_request_sent_by}` : ''}`}
                              style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem', fontWeight: 600, color: '#7c3aed', border: '1px solid #ddd6fe', backgroundColor: '#f5f3ff', borderRadius: '0.375rem' }}
                            >
                              ⭐ Review requested
                            </span>
                          ) : (
                            <Button
                              onClick={handleSendReviewRequest}
                              disabled={sendingReview}
                              data-testid="send-review-request-btn"
                              title="Send Google Review Request email to the customer"
                              style={{ backgroundColor: '#7c3aed', color: 'white' }}
                            >
                              {sendingReview ? '⏳...' : '⭐ Review'}
                            </Button>
                          )
                        )}
                        {/* Workflow shortcut: after emailing an invoice the tech
                            needs to slap a sticker on the device. Duplicating the
                            top-of-page Print Sticker button here saves a scroll
                            back up. Uses the same `printSticker` handler. */}
                        <Button
                          onClick={printSticker}
                          variant="outline"
                          data-testid="print-sticker-invoice-btn"
                          title="Reprint job sticker"
                        >
                          🏷️ Print Sticker
                        </Button>
                        <Button 
                          onClick={handleQuickGenerateInvoice}
                          disabled={savingBilling}
                          variant="outline"
                        >
                          🔄 Regenerate
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice Preview */}
                {showInvoicePreview && job.invoice_generated && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <iframe 
                      src={`${API}/jobs/${jobId}/invoice-view`}
                      style={{ 
                        width: '100%', 
                        height: '600px', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem'
                      }}
                      title="Invoice Preview"
                    />
                  </div>
                )}

                <h3 style={{ marginTop: '2rem' }}>
                  Payment Tracking
                  <HelpTooltip 
                    content="Record payments (full or partial), track outstanding balances, and send collection reminders. Payment status updates automatically based on recorded payments." 
                    side="right"
                  />
                </h3>
                
                {/* Prominent Call-to-Action when job is ready for collection.
                    Feb 2026 — Not Recoverable jobs are treated identically to
                    Not Proceeding (device returning to customer, typically with
                    a $0 balance): same red panel, same "Mark as Collected"
                    one-click close. Previously only Not Proceeding surfaced
                    this CTA which forced the user into Edit → Close for
                    Not Recoverable jobs. */}
                {(() => {
                  const isReturnState = job.status === 'Not Proceeding' || job.status === 'Not Recoverable';
                  const isFinishState = job.status === 'Finish' || job.status === 'Completed';
                  if (job.payment_status === 'Paid' || !(isFinishState || isReturnState)) return null;
                  return (
                  <div style={{
                    padding: '1rem 1.5rem',
                    marginBottom: '1rem',
                    backgroundColor: isReturnState ? '#fef2f2' : '#fef3c7',
                    borderRadius: '0.5rem',
                    border: `1px solid ${isReturnState ? '#fca5a5' : '#f59e0b'}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '1rem'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: isReturnState ? '#dc2626' : '#92400e', marginBottom: '0.25rem' }}>
                        {isReturnState ? '📦 Ready for Return' : '💰 Ready for Collection'}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isReturnState ? '#991b1b' : '#a16207' }}>
                        {isReturnState
                          ? (job.status === 'Not Recoverable'
                              ? 'Data not recoverable. Click to mark as collected when customer picks up.'
                              : 'Job not proceeding. Click to mark as collected when customer picks up.')
                          : 'Job is finished. Record payment when customer collects.'}
                      </div>
                    </div>
                    <Button 
                      onClick={async () => {
                        // Feb 2026 — for $0 balance Not Proceeding / Not
                        // Recoverable jobs, "Mark as Collected" should be a
                        // ONE-click action (no form dance). Post a synthetic
                        // $0 payment directly so the backend auto-closes the
                        // job, stamps date_out=today (Sydney) and flips
                        // payment_status to Paid — matching the visual
                        // "Collected (green)" state the user expects.
                        // For jobs with a real balance, keep the old flow
                        // (open the payment form).
                        const balance = parseFloat(job.balance_owing || 0);
                        if (balance <= 0) {
                          try {
                            const response = await axios.post(`${API}/jobs/${jobId}/payments`, {
                              amount: 0,
                              payment_date: todayLocalISO(),
                              payment_method: 'Collect',
                              reference_number: '',
                              notes: 'Marked as collected (zero balance).',
                            });
                            setJob(response.data);
                            setFormData(response.data);
                            toast.success(
                              `📦 Job #${job.job_number} marked as collected`,
                              { duration: 4000, description: `Closed on ${(response.data?.date_out || todayLocalISO()).slice(0, 10)}. Removed from Dashboard.` }
                            );
                          } catch (err) {
                            toast.error(`Failed to mark as collected: ${err?.response?.data?.detail || err.message}`);
                          }
                          return;
                        }
                        // Non-zero balance → still open the payment form.
                        setShowAddPayment(true);
                        setPaymentForm({
                          ...paymentForm,
                          amount: job.balance_owing?.toString() || '0'
                        });
                      }}
                      style={{ 
                        backgroundColor: isReturnState ? '#6b7280' : '#16a34a', 
                        color: 'white', 
                        fontWeight: '600' 
                      }}
                      data-testid="collect-payment-btn"
                    >
                      {isReturnState
                        ? '📦 Mark as Collected' 
                        : `💵 Collect Payment ($${job.balance_owing?.toFixed(2) || '0.00'})`}
                    </Button>
                  </div>
                  );
                })()}
                
                {/* Payment Summary Card */}
                <div className="payment-summary">
                  {/* Show RRP + Discount breakdown when a partner discount was applied.
                      total_amount below is the NET (post-discount) — same figure that drives payments/reports. */}
                  {job.discount_percent > 0 && job.rrp_total_amount > 0 && (
                    <>
                      <div className="payment-summary-item" data-testid="payment-summary-rrp">
                        <span className="label">RRP Total:</span>
                        <span className="value" style={{ color: '#475569' }}>${job.rrp_total_amount?.toFixed(2)}</span>
                      </div>
                      <div className="payment-summary-item" data-testid="payment-summary-discount">
                        <span className="label">Partner Discount ({job.discount_percent?.toFixed(2)}% on labour):</span>
                        <span className="value" style={{ color: '#b91c1c' }}>-${job.discount_amount?.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="payment-summary-item">
                    <span className="label">Total Amount:</span>
                    <span className="value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      ${job.total_amount?.toFixed(2) || '0.00'}
                      <button
                        onClick={() => {
                          setAdjustAmountForm({ new_amount: job.total_amount?.toString() || '0', reason: '' });
                          setShowAdjustAmount(true);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.125rem 0.25rem',
                          fontSize: '0.75rem',
                          color: '#6b7280',
                          textDecoration: 'underline'
                        }}
                        title="Adjust total amount"
                      >
                        (adjust)
                      </button>
                    </span>
                  </div>
                  <div className="payment-summary-item">
                    <span className="label">Amount Paid:</span>
                    <span className="value paid">${job.amount_paid?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="payment-summary-item">
                    <span className="label">Balance Owing:</span>
                    <span className={`value ${job.balance_owing > 0 ? 'owing' : 'paid'}`}>
                      ${job.balance_owing?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="payment-summary-item">
                    <span className="label">Status:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {/* Derived Payment State (Jul 2026) — one source of truth
                          based on revenue_date. Replaces the stale payment_status
                          field which was drifting out of sync.

                          Jul 9 update: when the job is in a closing status
                          (Finish / Not Recoverable / Not Proceeding) AND the
                          balance is $0 (nothing owed — e.g. a Not Recoverable
                          job with a $0 invoice, or a goodwill/warranty
                          Finish), we now surface the actual closing status on
                          the pill instead of wrongly falling through to "In
                          Progress". Previously such jobs looked open forever. */}
                      {(() => {
                        const AWAIT = ['Finish', 'Not Recoverable', 'Not Proceeding'];
                        const bal = parseFloat(job.balance_owing || 0);
                        let derived;
                        if (job.revenue_date) {
                          derived = { label: '💰 Paid', cls: 'paid' };
                        } else if (AWAIT.includes(job.status) && bal > 0) {
                          derived = { label: '⏳ Awaiting Payment', cls: 'awaiting-payment' };
                        } else if (AWAIT.includes(job.status) && bal <= 0 && job.closed) {
                          // Feb 2026 — $0 job that has been explicitly closed
                          // via "Mark as Collected" (or bulk-close). Show a
                          // distinct green "Collected" pill so the user gets
                          // visual confirmation that the customer picked up
                          // and the job is truly done. `closed=true` is the
                          // signal that separates "sitting on shelf" (grey)
                          // from "handed back to customer" (green).
                          derived = { label: '📦 Collected', cls: 'collected' };
                        } else if (AWAIT.includes(job.status) && bal <= 0) {
                          // Closed status with no balance but not yet collected
                          // — sitting on the shelf awaiting pickup.
                          if (job.status === 'Not Recoverable') derived = { label: '🚫 Not Recoverable', cls: 'closed' };
                          else if (job.status === 'Not Proceeding') derived = { label: '🚫 Not Proceeding', cls: 'closed' };
                          else derived = { label: '✓ Complete — No Charge', cls: 'closed' };
                        } else {
                          derived = { label: '🔧 In Progress', cls: 'unpaid' };
                        }
                        return (
                          <span
                            className={`payment-status-badge ${derived.cls}`}
                            title={derived.cls === 'awaiting-payment'
                              ? 'Job done and on shelf — customer to collect + pay.'
                              : derived.cls === 'paid'
                                ? `Paid & closed on ${(job.revenue_date || '').substring(0,10)}`
                                : derived.cls === 'collected'
                                  ? `Collected by customer on ${(job.date_out || '').substring(0,10)} — closed, no charge.`
                                  : derived.cls === 'closed'
                                    ? 'Job is closed and nothing is owed.'
                                    : 'Job is still being worked on.'}
                            data-testid="derived-payment-state-badge"
                            style={
                              derived.cls === 'awaiting-payment'
                                ? { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }
                                : derived.cls === 'collected'
                                  ? { backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #16a34a' }
                                  : derived.cls === 'closed'
                                    ? { backgroundColor: '#e5e7eb', color: '#334155', border: '1px solid #94a3b8' }
                                    : undefined
                            }
                          >
                            {derived.label}
                          </span>
                        );
                      })()}
                      {!showAddPayment && !job.revenue_date && (parseFloat(job.balance_owing || 0) > 0) && (
                        <button
                          type="button"
                          onClick={() => setShowAddPayment(true)}
                          className="add-payment-pill"
                          data-testid="add-payment-pill-btn"
                          title="Record a payment"
                        >
                          + Add Payment
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Feb 2026 — Fast Date Due dialog. Opens automatically when
                    the user flips Status to Approved on the Job Card, and
                    inline whenever they click the Date Due value. Saves via
                    PUT /api/jobs/{id} without needing Edit → Save. */}
                <Dialog open={dateDueDialogOpen} onOpenChange={setDateDueDialogOpen}>
                  <DialogContent style={{ maxWidth: '420px' }}>
                    <DialogHeader>
                      <DialogTitle>
                        {dateDueDialogContext === 'approval'
                          ? '✅ Job approved — when is it due?'
                          : '📅 Set Date Due'}
                      </DialogTitle>
                      <DialogDescription>
                        {dateDueDialogContext === 'approval'
                          ? 'Pick a due date so the job appears on your schedule. You can skip this and set it later.'
                          : 'Pick a new Date Due for this job. Saves immediately — no Edit needed.'}
                      </DialogDescription>
                    </DialogHeader>
                    <div style={{ display: 'grid', gap: '0.75rem', padding: '0.5rem 0 1rem' }}>
                      <div>
                        <Label htmlFor="date-due-dialog-input">Date Due</Label>
                        <Input
                          id="date-due-dialog-input"
                          type="date"
                          value={dateDueDialogValue}
                          onChange={(e) => setDateDueDialogValue(e.target.value)}
                          data-testid="date-due-dialog-input"
                          autoFocus
                        />
                      </div>
                      <div>
                        <Label style={{ fontSize: '0.8rem', color: '#6b7280' }}>Quick pick</Label>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                          {[
                            { label: 'Today',      days: 0 },
                            { label: 'Tomorrow',   days: 1 },
                            { label: '+2 days',    days: 2 },
                            { label: '+1 week',    days: 7 },
                          ].map(chip => (
                            <Button
                              key={chip.label}
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const d = new Date();
                                d.setDate(d.getDate() + chip.days);
                                setDateDueDialogValue(dateToLocalISO(d));
                              }}
                              data-testid={`date-due-chip-${chip.days}`}
                              style={{ padding: '0.25rem 0.7rem', fontSize: '0.8rem' }}
                            >
                              {chip.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <DialogFooter style={{ gap: '0.5rem' }}>
                      {dateDueDialogContext === 'approval' && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={skipDateDueFromDialog}
                          disabled={savingDateDue}
                          data-testid="date-due-dialog-skip"
                        >
                          Skip
                        </Button>
                      )}
                      {dateDueDialogContext !== 'approval' && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setDateDueDialogOpen(false)}
                          disabled={savingDateDue}
                          data-testid="date-due-dialog-cancel"
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        type="button"
                        onClick={saveDateDueFromDialog}
                        disabled={savingDateDue || !dateDueDialogValue}
                        style={{ backgroundColor: '#16a34a', color: 'white' }}
                        data-testid="date-due-dialog-save"
                      >
                        {savingDateDue ? 'Saving…' : 'Save'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Adjust Amount Dialog */}
                <Dialog open={showAdjustAmount} onOpenChange={setShowAdjustAmount}>
                  <DialogContent style={{ maxWidth: '400px' }}>
                    <DialogHeader>
                      <DialogTitle>Adjust Total Amount</DialogTitle>
                      <DialogDescription>
                        Change the total amount for this job. Use this to write off a balance, apply a discount, or correct an error.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAdjustAmount}>
                      <div style={{ display: 'grid', gap: '1rem', padding: '1rem 0' }}>
                        <div>
                          <Label>Current Amount</Label>
                          <div style={{ fontWeight: '600', fontSize: '1.125rem', color: '#6b7280' }}>
                            ${job.total_amount?.toFixed(2) || '0.00'}
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="new-amount">New Amount *</Label>
                          <Input
                            id="new-amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={adjustAmountForm.new_amount}
                            onChange={(e) => setAdjustAmountForm({ ...adjustAmountForm, new_amount: e.target.value })}
                            required
                            placeholder="0.00"
                          />
                          {parseFloat(adjustAmountForm.new_amount) === 0 && (
                            <p style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                              Setting to $0 will write off the entire balance
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="adjust-reason">Reason (optional)</Label>
                          <Input
                            id="adjust-reason"
                            type="text"
                            value={adjustAmountForm.reason}
                            onChange={(e) => setAdjustAmountForm({ ...adjustAmountForm, reason: e.target.value })}
                            placeholder="e.g., Customer declined, Discount applied, Pricing error"
                          />
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            This will be added as a private note
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                        <Button type="button" variant="outline" onClick={() => setShowAdjustAmount(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={savingAdjustment}>
                          {savingAdjustment ? 'Saving...' : 'Update Amount'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Add Payment Button (full-size, only shown after the pill is used to expand) */}
                {/* Pill button next to status now handles the trigger */}

                {/* Add Payment Form */}
                {showAddPayment && (
                  <Card style={{ marginTop: '1rem', padding: '1.5rem' }}>
                    <h4 style={{ marginBottom: '1rem' }}>
                      {editingPaymentId ? 'Edit Payment' : 'Record Payment'}
                    </h4>
                    <form onSubmit={handleAddPayment} className="payment-form">
                      <div className="form-row">
                        <div className="form-field">
                          <Label htmlFor="payment-amount">Amount *</Label>
                          <Input
                            id="payment-amount"
                            type="number"
                            step="0.01"
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                            required
                            placeholder="0.00"
                          />
                        </div>
                        <div className="form-field">
                          <Label htmlFor="payment-date">Date *</Label>
                          <Input
                            id="payment-date"
                            type="date"
                            value={paymentForm.payment_date}
                            onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-field">
                          <Label htmlFor="payment-method">Payment Method *</Label>
                          <Select
                            value={paymentForm.payment_method}
                            onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(settings?.incoming_payment_methods && settings.incoming_payment_methods.length > 0
                                ? settings.incoming_payment_methods
                                : ['Cash', 'Card', 'Bank Transfer', 'Other']).map((m) => (
                                  <SelectItem key={m} value={m}>{m}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="form-field">
                          <Label htmlFor="payment-reference">Reference Number</Label>
                          <Input
                            id="payment-reference"
                            value={paymentForm.reference_number}
                            onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                            placeholder="Transaction/Receipt #"
                          />
                        </div>
                      </div>

                      <div className="form-field">
                        <Label htmlFor="payment-notes">Notes</Label>
                        <Textarea
                          id="payment-notes"
                          value={paymentForm.notes}
                          onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                          placeholder="Additional payment details..."
                          rows={2}
                        />
                      </div>

                      <div className="form-actions" style={{ marginTop: '1rem' }}>
                        <Button type="submit" data-testid="save-payment-btn">
                          {editingPaymentId ? 'Update Payment' : 'Save Payment'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => {
                            setShowAddPayment(false);
                            setEditingPaymentId(null);
                            setPaymentForm({
                              amount: '',
                              payment_date: todayLocalISO(),
                              payment_method: 'Card',
                              reference_number: '',
                              notes: ''
                            });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </Card>
                )}

                {/* Payment History */}
                {job.payments && job.payments.length > 0 && (
                  <div className="payment-history" style={{ marginTop: '1.5rem' }}>
                    <h4>Payment History</h4>
                    <div className="payments-list">
                      {job.payments.map((payment) => (
                        <div key={payment.id} className="payment-item" data-testid={`payment-item-${payment.id}`}>
                          <div className="payment-item-header">
                            <div className="payment-amount">${payment.amount.toFixed(2)}</div>
                            <div className="payment-method">{payment.payment_method}</div>
                            <div style={{ display: 'flex', gap: '0.35rem', marginLeft: 'auto' }}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleStartEditPayment(payment)}
                                data-testid={`edit-payment-${payment.id}`}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeletePayment(payment.id)}
                                data-testid={`delete-payment-${payment.id}`}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                          <div className="payment-item-details">
                            <span className="payment-date">
                              {formatDate(payment.payment_date)}
                            </span>
                            {payment.reference_number && (
                              <span className="payment-reference">Ref: {payment.reference_number}</span>
                            )}
                          </div>
                          {payment.notes && (
                            <div className="payment-notes">{payment.notes}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Financial Information Section */}
                <div className="detail-section" style={{ marginTop: '2rem' }}>
                  <h3>Financial Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Total Amount (inc. GST):</span>
                      <span className="detail-value">${parseFloat(job.total_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">GST %:</span>
                      <span className="detail-value">{parseFloat(job.gst_percentage || 10).toFixed(2)}%</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Subtotal (ex. GST):</span>
                      <span className="detail-value">${parseFloat(job.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">GST Amount:</span>
                      <span className="detail-value">${parseFloat(job.gst_amount || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Email Send History */}
            {!editing && job.invoice_emails_sent && job.invoice_emails_sent.length > 0 && (
              <div className="email-history-section" style={{ 
                marginTop: '2rem',
                padding: '1.5rem', 
                backgroundColor: '#f8fafc', 
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem'
              }}>
                <h3 style={{ 
                  marginBottom: '1rem', 
                  fontSize: '1.25rem', 
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#1e40af'
                }}>
                  <span>📧</span>
                  <span>Email History</span>
                  <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>
                    ({job.invoice_emails_sent.length})
                  </span>
                </h3>
                <div>
                  {job.invoice_emails_sent.slice().reverse().map((log, index) => {
                    // Map reversed-display index back to the original storage index
                    const storageIndex = job.invoice_emails_sent.length - 1 - index;
                    const canResend = !!(log.body_html || log.body_text);
                    const isResendingThisOne = resendingEmailIndex === storageIndex;
                    const isResend = typeof log.email_type === 'string' && log.email_type.startsWith('resend:');
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          // Default the preview view to "message" when the text
                          // body exists (that's the operator's own template
                          // content — bank details, personalised notes). Fall
                          // back to invoice HTML if there is no text body.
                          setEmailPreviewView(log.body_text ? 'message' : 'invoice');
                          setEmailPreview({ open: true, log });
                        }}
                        data-testid={`email-history-item-${index}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEmailPreviewView(log.body_text ? 'message' : 'invoice'); setEmailPreview({ open: true, log }); } }}
                        style={{
                          padding: '0.75rem',
                          marginBottom: index < job.invoice_emails_sent.length - 1 ? '0.5rem' : '0',
                          backgroundColor: '#ffffff',
                          borderRadius: '0.375rem',
                          border: '1px solid #e2e8f0',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s, border-color 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                      >
                        <div style={{ fontWeight: '600', color: '#0f172a', marginBottom: '0.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
                            {isResend && (
                              <span
                                title="This entry was itself a resend"
                                style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '999px', fontWeight: 600, letterSpacing: '0.02em' }}
                              >
                                ↻ RESENT
                              </span>
                            )}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.subject || `${log.email_type === 'invoice' ? 'Invoice' : log.email_type === 'quote' ? 'Quote' : 'Job Update'} Email`}
                            </span>
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                            <span style={{ fontSize: '0.75rem', color: '#2563eb', fontWeight: 500 }}>👁️ View</span>
                            {canResend && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); resendEmail(storageIndex, log); }}
                                disabled={resendingEmailIndex !== null}
                                data-testid={`resend-email-btn-${index}`}
                                style={{
                                  padding: '0.15rem 0.55rem',
                                  fontSize: '0.75rem',
                                  height: 'auto',
                                  lineHeight: 1.2,
                                }}
                                title={`Resend this exact email to ${log.sent_to || job.email || 'the customer'}`}
                              >
                                {isResendingThisOne ? '⏳ Sending…' : '↻ Resend'}
                              </Button>
                            )}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#475569' }}>
                          To: {log.sent_to}
                          {Array.isArray(log.cc) && log.cc.length > 0 && <span> · CC: {log.cc.join(', ')}</span>}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '0.8125rem', marginTop: '0.25rem' }}>
                          {new Date(log.sent_at).toLocaleString('en-AU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SMS History */}
            {!editing && job.sms_history && job.sms_history.length > 0 && (
              <div className="sms-history-section" style={{ 
                marginTop: '2rem',
                padding: '1.5rem', 
                backgroundColor: '#f8fafc', 
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem'
              }}>
                <h3 style={{ 
                  marginBottom: '1rem', 
                  fontSize: '1.25rem', 
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#166534'
                }}>
                  <span>📱</span>
                  <span>SMS History</span>
                  <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>
                    ({job.sms_history.length})
                  </span>
                </h3>
                <div>
                  {job.sms_history.slice().reverse().map((log, index) => (
                    <div 
                      key={index}
                      style={{ 
                        padding: '0.75rem',
                        marginBottom: index < job.sms_history.length - 1 ? '0.5rem' : '0',
                        backgroundColor: log.type === 'sms_received' ? '#f0fdf4' : '#ffffff',
                        borderRadius: '0.375rem',
                        border: `1px solid ${log.type === 'sms_received' ? '#86efac' : '#e2e8f0'}`,
                        borderLeft: log.type === 'sms_received' ? '4px solid #22c55e' : '4px solid #3b82f6'
                      }}
                    >
                      <div style={{ fontWeight: '600', color: '#0f172a', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {log.type === 'sms_received' ? (
                          <>
                            <span style={{ color: '#22c55e' }}>↙️ Received</span>
                            <span style={{ fontWeight: '400', color: '#6b7280' }}>from {log.from}</span>
                          </>
                        ) : (
                          <>
                            <span style={{ color: '#3b82f6' }}>↗️ Sent</span>
                            <span style={{ fontWeight: '400', color: '#6b7280' }}>to {log.to}</span>
                          </>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: '#475569',
                        backgroundColor: log.type === 'sms_received' ? '#dcfce7' : '#f8fafc',
                        padding: '0.5rem',
                        borderRadius: '0.25rem',
                        marginBottom: '0.5rem',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {log.message}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        color: '#64748b', 
                        fontSize: '0.8125rem'
                      }}>
                        <span>{log.type === 'sms_received' ? 'Customer reply' : `Sent by ${log.sent_by}`}</span>
                        <span>
                          {new Date(log.timestamp).toLocaleString('en-AU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Terms and Conditions intentionally moved to the bottom of the
                page (below Activity History) — see further down. T&C are for
                the customer; the operator doesn't need them in the daily flow. */}

            {/* Unified Signature Section */}
            {!editing && (
              <Card style={{ marginTop: '1.5rem' }}>
                <CardHeader>
                  <CardTitle style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    ✍️ Customer Signature
                    <HelpTooltip 
                      content="Capture customer signatures via iPad kiosk (in-person) or email link (remote). Signed jobs show the signature image and timestamp." 
                      side="right"
                    />
                    {(job.kiosk_signature || job.signature) ? (
                      <span style={{ 
                        padding: '0.25rem 0.75rem', 
                        background: '#dcfce7', 
                        color: '#166534',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {job.kiosk_signature ? '✓ Signed on iPad' : '✓ Signed'}
                      </span>
                    ) : (
                      <span style={{ 
                        padding: '0.25rem 0.75rem', 
                        background: '#fef2f2', 
                        color: '#dc2626',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        ✗ Not Signed
                      </span>
                    )}
                  </CardTitle>
                  {job.kiosk_signature && (
                    <CardDescription>
                      Signed electronically on iPad: {formatUtcLocal(job.kiosk_signed_at)}
                    </CardDescription>
                  )}
                  {job.signature && !job.kiosk_signature && (
                    <CardDescription>
                      Signed on: {formatUtcLocal(job.signature_date)}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Recovery banner — jobs where the activity log says "signature_received"
                      but no signature image is stored. Root cause: blank-canvas submit slipped
                      past validation historically. The reissue endpoint clears the empty
                      signature + generates a fresh token so the customer can sign again. */}
                  {!job.kiosk_signature && !job.signature && Array.isArray(job.activity_log) &&
                   job.activity_log.some((e) => e && e.type === 'signature_received') && (
                    <div
                      data-testid="signature-missing-warning"
                      style={{
                        padding: '0.85rem 1rem',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fca5a5',
                        borderLeft: '4px solid #dc2626',
                        borderRadius: '6px',
                        marginBottom: '1rem',
                      }}
                    >
                      <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: '0.35rem' }}>
                        ⚠️ Signature was received but not saved
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#7f1d1d', marginBottom: '0.75rem' }}>
                        The activity log shows the customer opened the sign link and hit Submit, but the signature image was blank when it arrived. Server-side validation is now in place to prevent this — but this older job needs a fresh link so the customer can sign again.
                      </div>
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/jobs/${jobId}/signature/reissue`);
                            const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api$/, '');
                            const fullUrl = `${backend}${res.data.signature_url}`;
                            setSignatureLink(fullUrl);
                            toast.success('Fresh signature link generated. Copy it below and send it to the customer.');
                            fetchJob();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Could not reissue signature link');
                          }
                        }}
                        data-testid="reissue-signature-link-btn"
                      >
                        Re-issue Signature Link
                      </Button>
                    </div>
                  )}
                  {/* Show iPad signature if available */}
                  {job.kiosk_signature && (
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '1rem',
                      background: '#f9fafb',
                      borderRadius: '0.5rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <img 
                        src={job.kiosk_signature} 
                        alt="Customer Signature" 
                        style={{ 
                          maxWidth: '400px', 
                          maxHeight: '200px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.25rem',
                          background: 'white'
                        }}
                        data-testid="kiosk-signature-image"
                      />
                      {job.kiosk_email_sent && (
                        <p style={{ 
                          fontSize: '0.875rem', 
                          color: '#6b7280', 
                          marginTop: '0.75rem'
                        }}>
                          ✉️ Job card email sent to customer
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Show remote signature if available (and no iPad signature) */}
                  {job.signature && !job.kiosk_signature && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      padding: '1rem',
                      background: '#f9fafb',
                      borderRadius: '0.5rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <img 
                        src={job.signature} 
                        alt="Customer Signature" 
                        style={{ 
                          maxWidth: '400px', 
                          maxHeight: '200px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.25rem',
                          background: 'white'
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Show signature options if not signed */}
                  {!job.kiosk_signature && !job.signature && (
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                        No signature captured yet. Use one of the options below:
                      </p>
                      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button 
                          onClick={handleGenerateSignatureLink}
                          variant="outline"
                          data-testid="generate-signature-link-btn"
                        >
                          📧 Generate Signature Link (Remote)
                        </Button>
                        <span style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          color: '#9ca3af',
                          fontSize: '0.875rem'
                        }}>
                          or use "📱 Send to iPad" button above
                        </span>
                      </div>
                      
                      {signatureLink && (
                        <div style={{ 
                          marginTop: '1.5rem', 
                          padding: '1rem', 
                          background: '#f0f9ff', 
                          borderRadius: '0.5rem',
                          border: '1px solid #bae6fd',
                          textAlign: 'left'
                        }}>
                          <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>📋 Signature Link:</p>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <Input 
                              ref={signatureLinkInputRef}
                              value={signatureLink} 
                              readOnly 
                              style={{ flex: 1 }}
                              onClick={(e) => e.target.select()}
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                const linkToCopy = signatureLinkInputRef.current?.value || signatureLink;
                                navigator.clipboard.writeText(linkToCopy)
                                  .then(() => {
                                    toast.success('✅ Link copied to clipboard!');
                                  })
                                  .catch(() => {
                                    try {
                                      const input = signatureLinkInputRef.current;
                                      if (input) {
                                        input.select();
                                        input.setSelectionRange(0, 99999);
                                        const successful = document.execCommand('copy');
                                        if (successful) {
                                          toast.success('✅ Link copied!');
                                        } else {
                                          toast.info('📋 Link selected - press Ctrl+C to copy');
                                        }
                                      }
                                    } catch (err) {
                                      toast.info('📋 Link selected - press Ctrl+C to copy');
                                    }
                                  });
                              }}
                            >
                              Copy
                            </Button>
                          </div>
                          <p style={{ fontSize: '0.875rem', color: '#0369a1', marginTop: '0.75rem' }}>
                            📱 Send this link to the customer via email or SMS. They can sign from any device.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Comments Timeline Section */}
            {!editing && (
              <div className="comments-section">
                <h3>
                  Comments History
                  <HelpTooltip 
                    content="Add notes and track job progress. Set follow-up dates to create reminders. Mark comments as 'Private' to exclude from customer emails." 
                    side="right"
                  />
                </h3>
                <div className="comments-timeline">
                  {job.comments && job.comments.length > 0 ? (
                    [...job.comments]
                      .sort((a, b) => {
                        const ta = new Date(a.created_at || a.timestamp || 0).getTime();
                        const tb = new Date(b.created_at || b.timestamp || 0).getTime();
                        return tb - ta; // newest first
                      })
                      .map((comment, index) => (
                      <div key={index} className="comment-item" data-testid={`comment-${index}`} style={{
                        textDecoration: comment.completed ? 'line-through' : 'none',
                        opacity: comment.completed ? 0.6 : 1
                      }}>
                        <div className="comment-icon">{comment.follow_up_date && !comment.completed ? '📅' : '💬'}</div>
                        <div className="comment-content">
                          <div className="comment-text">{comment.text}</div>
                          <div className="comment-timestamp">
                            {formatDateTime(comment.timestamp)}
                            {comment.follow_up_date && !comment.completed && (
                              <span style={{ marginLeft: '1rem', color: '#10b981', fontWeight: '600' }}>
                                📅 Reminder: {comment.follow_up_date}
                              </span>
                            )}
                            {comment.completed && (
                              <span style={{ marginLeft: '1rem', color: '#666' }}>
                                ✓ Completed
                              </span>
                            )}
                          </div>
                          {comment.follow_up_date && !comment.completed && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCompleteReminder(index)}
                              style={{ marginTop: '0.5rem' }}
                            >
                              Mark Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-comments">No comments yet</div>
                  )}
                </div>
                
                <div className="add-comment-section">
                  <Label htmlFor="new-comment">Add New Comment</Label>
                  <Textarea
                    id="new-comment"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Type your comment here..."
                    rows={3}
                    data-testid="new-comment-input"
                  />
                  <div style={{ marginTop: '0.5rem' }}>
                    <Label htmlFor="follow-up-date">Follow-up Date (Optional)</Label>
                    <Input
                      id="follow-up-date"
                      type="date"
                      value={followUpDate}
                      onChange={(e) => setFollowUpDate(e.target.value)}
                      style={{ marginTop: '0.25rem' }}
                    />
                    <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                      Set a reminder date to follow up on this comment
                    </p>
                  </div>
                  <Button 
                    onClick={handleAddComment} 
                    disabled={addingComment || !newComment.trim()}
                    data-testid="add-comment-button"
                    style={{ marginTop: '0.5rem' }}
                  >
                    {addingComment ? 'Adding...' : 'Add Comment'}
                  </Button>
                </div>
              </div>
            )}

            {/* Activity Log Section */}
            {!editing && job.activity_log && job.activity_log.length > 0 && (
              <Card style={{ marginTop: '1.5rem' }}>
                <CardHeader>
                  <CardTitle style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    📋 Activity History
                    <HelpTooltip 
                      content="Complete timeline of all actions taken on this job including status changes, emails sent, payments recorded, and more." 
                      side="right"
                    />
                    <span style={{ 
                      fontSize: '0.75rem', 
                      fontWeight: 'normal', 
                      backgroundColor: '#e0e7ff', 
                      color: '#4338ca',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '999px',
                      marginLeft: '0.5rem'
                    }}>
                      {job.activity_log.length} events
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}>
                    {[...job.activity_log].reverse().map((activity, index) => (
                      <div 
                        key={activity.id || index}
                        style={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          borderBottom: index < job.activity_log.length - 1 ? '1px solid #f3f4f6' : 'none',
                          backgroundColor: index === 0 ? '#f0fdf4' : 'transparent'
                        }}
                      >
                        <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>{activity.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            gap: '0.25rem'
                          }}>
                            <span style={{ fontWeight: '600', color: '#1e40af' }}>
                              {activity.label}
                            </span>
                            <span style={{ 
                              fontSize: '0.75rem', 
                              color: '#6b7280',
                              whiteSpace: 'nowrap'
                            }}>
                              {new Date(activity.timestamp).toLocaleDateString('en-AU', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {activity.details && (
                            <p style={{ 
                              margin: '0.25rem 0 0 0', 
                              fontSize: '0.875rem', 
                              color: '#4b5563',
                              wordBreak: 'break-word'
                            }}>
                              {activity.details}
                            </p>
                          )}
                          {activity.user && activity.user !== 'System' && (
                            <span style={{ 
                              fontSize: '0.75rem', 
                              color: '#9ca3af',
                              marginTop: '0.25rem',
                              display: 'inline-block'
                            }}>
                              by {activity.user}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Terms and Conditions — moved to bottom of page, collapsed by default.
                Operators rarely need to re-read these; customers see them in the
                signature flow. Shows a green "✓ Accepted" badge if the customer
                has already signed (kiosk or remote) so you get reassurance at a
                glance without expanding the section. */}
            {!editing && settings?.terms_and_conditions && (
              <Collapsible style={{ marginTop: '1.5rem' }} data-testid="terms-collapsible">
                <div style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                }}>
                  <CollapsibleTrigger
                    data-testid="terms-toggle"
                    style={{
                      width: '100%',
                      padding: '1rem 1.25rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: '#1e293b',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      📜 Terms and Conditions
                      {(job.kiosk_signature || job.signature) && (
                        <span style={{
                          padding: '0.15rem 0.6rem',
                          background: '#dcfce7',
                          color: '#166534',
                          borderRadius: '9999px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                        }}>
                          ✓ Accepted{(() => {
                            const iso = job.kiosk_signed_at || job.signature_date;
                            if (!iso) return '';
                            const s = String(iso);
                            const d = new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + 'Z');
                            return Number.isNaN(d.getTime()) ? '' : ` on ${d.toLocaleDateString('en-AU')}`;
                          })()}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>▾ Show / hide</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div style={{
                      padding: '0 1.25rem 1.25rem',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      color: '#475569',
                    }}>
                      {settings.terms_and_conditions}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Stock Item Dialog */}
      <Dialog open={showAddStock} onOpenChange={setShowAddStock}>
        <DialogContent style={{ maxWidth: '600px' }}>
          <DialogHeader>
            <DialogTitle>Add Stock Item to Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStock}>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Stock Item *
                </label>
                <select
                  required
                  value={stockForm.stock_item_id}
                  onChange={(e) => handleStockItemChange(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem'
                  }}
                >
                  <option value="">-- Select Stock Item --</option>
                  {stockItems.filter(item => item.current_quantity > 0).map(item => (
                    <option key={item.id} value={item.id}>
                      {item.brand ? `${item.brand} - ` : ''}{item.item_name} (Available: {item.current_quantity})
                    </option>
                  ))}
                </select>
                {stockForm.stock_item_id && stockItems.find(item => item.id === stockForm.stock_item_id) && (
                  <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                    Available: {stockItems.find(item => item.id === stockForm.stock_item_id).current_quantity} units
                  </p>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Quantity *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={stockForm.quantity}
                    onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })}
                    placeholder="1"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Sell Price (per unit) *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={stockForm.sell_price}
                    onChange={(e) => setStockForm({ ...stockForm, sell_price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {stockForm.stock_item_id && stockForm.quantity > 0 && stockForm.sell_price >= 0 && (
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#f0f9ff', 
                  borderRadius: '0.5rem',
                  border: '1px solid #bae6fd'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Cost Price (per unit):</span>
                    <span style={{ fontWeight: '600' }}>
                      ${stockItems.find(item => item.id === stockForm.stock_item_id)?.cost_price.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Total Sell:</span>
                    <span style={{ fontWeight: '600' }}>
                      ${(parseFloat(stockForm.quantity) * parseFloat(stockForm.sell_price)).toFixed(2)}
                    </span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    paddingTop: '0.5rem',
                    borderTop: '2px solid #93c5fd',
                    marginTop: '0.5rem'
                  }}>
                    <span style={{ fontWeight: '600' }}>Profit:</span>
                    <span style={{ 
                      fontWeight: 'bold',
                      fontSize: '1.125rem',
                      color: (parseFloat(stockForm.quantity) * parseFloat(stockForm.sell_price) - 
                             parseFloat(stockForm.quantity) * (stockItems.find(item => item.id === stockForm.stock_item_id)?.cost_price || 0)) >= 0
                        ? '#16a34a' 
                        : '#dc2626'
                    }}>
                      ${(parseFloat(stockForm.quantity) * parseFloat(stockForm.sell_price) - 
                         parseFloat(stockForm.quantity) * (stockItems.find(item => item.id === stockForm.stock_item_id)?.cost_price || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <Button type="button" variant="outline" onClick={() => setShowAddStock(false)}>
                Cancel
              </Button>
              <Button type="submit">Add to Job</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Generate Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent style={{ maxWidth: '600px' }}>
          <DialogHeader>
            <DialogTitle>{job.invoice_generated ? 'Edit Invoice' : 'Generate Invoice'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateInvoice}>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Invoice Details:</div>
                <div>Invoice #: {job.job_number}</div>
                <div>Customer: {job.customer_name}</div>
                <div>Job Type: {job.job_type}</div>
                <div>Total Amount: ${job.total_amount?.toFixed(2) || '0.00'}</div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Work Performed *
                </label>
                <textarea
                  required
                  value={invoiceForm.work_performed}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, work_performed: e.target.value })}
                  placeholder="Describe the work completed on this job..."
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit'
                  }}
                />
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  This will appear on the invoice under the job type
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Payment Terms
                </label>
                <Input
                  value={invoiceForm.invoice_terms}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_terms: e.target.value })}
                  placeholder="e.g., Payment due upon receipt, Net 30, etc."
                />
              </div>

              <div style={{ 
                padding: '0.75rem', 
                backgroundColor: '#fef3c7', 
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                border: '1px solid #fbbf24'
              }}>
                <strong>Note:</strong> Original issue description and stock items (if any) will automatically be included on the invoice.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <Button type="button" variant="outline" onClick={() => setShowInvoiceDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" style={{ backgroundColor: '#16a34a' }}>
                {job.invoice_generated ? 'Update Invoice' : 'Generate Invoice'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quote Email Dialog — edit personal message + CC/BCC before sending */}
      <Dialog open={showQuoteEmailDialog} onOpenChange={setShowQuoteEmailDialog}>
        <DialogContent style={{ maxWidth: '680px' }}>
          <DialogHeader>
            <DialogTitle>Send Quote Email</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '0.5rem 0', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div style={{ padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem', fontSize: '0.85rem' }}>
              <strong>Quote for Job #{job?.job_number}</strong> · To: <strong>{job?.email}</strong>
              <br/><span style={{ color: '#475569' }}>The standard quote card (job details, approval button, etc.) is always included — your personal message appears above it. Long messages render as a clean body block; short notes render as a highlighted sticky note.</span>
            </div>

            {/* Template picker — Standard + any custom templates configured in
                Settings → Email Templates → Custom Quote Templates. Switching
                template repopulates the personal-message textarea (and the
                deposit field if the template body uses the {deposit}
                placeholder). The textarea remains editable after switching. */}
            {(settings?.email_templates?.quote_custom_templates || []).length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '0.75rem', alignItems: 'end' }}>
                <div>
                  <Label htmlFor="quote-template-picker">Template</Label>
                  <select
                    id="quote-template-picker"
                    value={quoteTemplateId}
                    onChange={(e) => switchQuoteTemplate(e.target.value)}
                    data-testid="quote-template-picker"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                    }}
                  >
                    <option value="standard">Standard (default personal message)</option>
                    {(settings?.email_templates?.quote_custom_templates || []).map((t) => (
                      <option key={t.id} value={t.id}>{t.name || t.id}</option>
                    ))}
                  </select>
                </div>
                {/* Deposit input only shown when the picked template's body uses {deposit} */}
                {quoteTemplateId !== 'standard' && (() => {
                  const picked = (settings?.email_templates?.quote_custom_templates || []).find((t) => t.id === quoteTemplateId);
                  if (!picked || !(picked.body || '').includes('{deposit}')) return null;
                  return (
                    <div>
                      <Label htmlFor="quote-deposit">Deposit ($)</Label>
                      <Input
                        id="quote-deposit"
                        type="number"
                        min="0"
                        step="1"
                        value={quoteDeposit}
                        onChange={(e) => applyDepositChange(e.target.value)}
                        data-testid="quote-email-deposit"
                        placeholder="350"
                      />
                    </div>
                  );
                })()}
              </div>
            )}

            <div>
              <Label htmlFor="quote-personal-msg">Personal message (editable)</Label>
              <Textarea
                id="quote-personal-msg"
                rows={quoteTemplateId === 'standard' ? 5 : 14}
                placeholder="Hi John, attached is the quote we discussed Tuesday — let me know your thoughts."
                value={quotePersonalMsg}
                onChange={(e) => setQuotePersonalMsg(e.target.value)}
                data-testid="quote-email-personal-msg"
                style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <Label htmlFor="quote-cc">CC (comma-separated)</Label>
                <Input id="quote-cc" value={quoteCc} onChange={(e) => setQuoteCc(e.target.value)} data-testid="quote-email-cc" />
              </div>
              <div>
                <Label htmlFor="quote-bcc">BCC (comma-separated)</Label>
                <Input id="quote-bcc" value={quoteBcc} onChange={(e) => setQuoteBcc(e.target.value)} data-testid="quote-email-bcc" />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => setShowQuoteEmailDialog(false)}>Cancel</Button>
            <Button
              type="button"
              onClick={confirmEmailQuote}
              disabled={emailingQuote}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              data-testid="quote-email-send-btn"
            >
              📧 Send Quote
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Confirmation Dialog — editable subject/body/CC/BCC */}
      <Dialog open={showEmailConfirm} onOpenChange={setShowEmailConfirm}>
        <DialogContent style={{ maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>Send Invoice Email</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '0.5rem 0', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div style={{ padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem', fontSize: '0.85rem' }}>
              <strong>Invoice #{job?.job_number}</strong> · Amount <strong>${job?.total_amount?.toFixed(2) || '0.00'}</strong>
              <br/>
              <span style={{ color: '#475569' }}>To: <strong>{job?.email}</strong></span>
            </div>
            {/* Feb 2026 — Template dropdown. Pre-selected based on payment
                status (paid → Paid Tax Invoice, else standard Invoice).
                Switching this re-fills subject/body/cc/bcc from the chosen
                template. */}
            <div>
              <Label htmlFor="invoice-template-key">Template</Label>
              <select
                id="invoice-template-key"
                data-testid="invoice-template-key-select"
                value={invoiceTemplateKey}
                onChange={(e) => {
                  const k = e.target.value;
                  setInvoiceTemplateKey(k);
                  applyInvoiceTemplate(k);
                }}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', background: 'white' }}
              >
                <option value="invoice_email">Invoice (payment instructions)</option>
                <option value="paid_invoice_email">Paid Tax Invoice (thank-you note)</option>
              </select>
              <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                Auto-selected based on payment status. Change if you want to override.
              </p>
            </div>
            <div>
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                data-testid="invoice-email-subject"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <Label htmlFor="email-cc">CC (comma-separated)</Label>
                <Input
                  id="email-cc"
                  placeholder="accountant@example.com"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                  data-testid="invoice-email-cc"
                />
              </div>
              <div>
                <Label htmlFor="email-bcc">BCC (comma-separated)</Label>
                <Input
                  id="email-bcc"
                  placeholder="archive@example.com"
                  value={emailBcc}
                  onChange={(e) => setEmailBcc(e.target.value)}
                  data-testid="invoice-email-bcc"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email-body">Message</Label>
              <Textarea
                id="email-body"
                rows={14}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                data-testid="invoice-email-body"
                style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
              />
              <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                The full invoice (with totals, line items, GST) will be appended below this message automatically.
                Edit the default in Settings → Email Templates → Invoice Email.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={cancelSendEmail}>
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={confirmSendEmail}
              disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              data-testid="invoice-email-send-btn"
            >
              📧 Send Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Review Request confirm dialog. Shows the rendered
          subject + body from the server so the operator can eyeball what
          the customer will see before hitting Send. Safe-by-default. */}
      <Dialog open={showReviewConfirm} onOpenChange={setShowReviewConfirm}>
        <DialogContent style={{ maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }} data-testid="review-request-dialog">
          <DialogHeader>
            <DialogTitle>⭐ Send Google Review Request</DialogTitle>
          </DialogHeader>
          {reviewPreview && (
            <div style={{ padding: '0.5rem 0', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div style={{ padding: '0.75rem', backgroundColor: '#f5f3ff', borderRadius: '0.5rem', fontSize: '0.85rem' }}>
                <strong>To:</strong> {reviewPreview.to}<br />
                <strong>Subject:</strong> {reviewPreview.subject}
              </div>
              <div>
                <Label>Message preview</Label>
                <div style={{
                  whiteSpace: 'pre-wrap',
                  padding: '0.85rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  fontSize: '0.85rem',
                  fontFamily: 'inherit',
                  backgroundColor: 'white',
                  maxHeight: '340px',
                  overflowY: 'auto',
                }} data-testid="review-request-body-preview">
                  {reviewPreview.body}
                </div>
                <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                  Edit the template + review URL under Settings → Email Templates.
                </p>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => { setShowReviewConfirm(false); setReviewPreview(null); }} disabled={sendingReview} data-testid="review-request-cancel-btn">
              Cancel
            </Button>
            <Button type="button" onClick={confirmSendReviewRequest} disabled={sendingReview} style={{ backgroundColor: '#7c3aed', color: 'white' }} data-testid="review-request-send-btn">
              {sendingReview ? '⏳ Sending...' : '⭐ Send'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Preview — shows the exact HTML/text body that was sent to the customer */}
      <Dialog open={emailPreview.open} onOpenChange={(open) => !open && setEmailPreview({ open: false, log: null })}>
        <DialogContent style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }} data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle data-testid="email-preview-subject">
              {emailPreview.log?.subject || 'Email Preview'}
            </DialogTitle>
            <DialogDescription>
              {emailPreview.log?.email_type ? `Type: ${emailPreview.log.email_type}` : ''}
              {emailPreview.log?.sent_at && (
                <>
                  {emailPreview.log?.email_type ? ' · ' : ''}
                  Sent {new Date(emailPreview.log.sent_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {emailPreview.log && (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '6px', fontSize: '0.875rem', border: '1px solid #e2e8f0' }}>
                <div><strong>To:</strong> {emailPreview.log.sent_to}</div>
                {Array.isArray(emailPreview.log.cc) && emailPreview.log.cc.length > 0 && <div><strong>CC:</strong> {emailPreview.log.cc.join(', ')}</div>}
                {Array.isArray(emailPreview.log.bcc) && emailPreview.log.bcc.length > 0 && <div><strong>BCC:</strong> {emailPreview.log.bcc.join(', ')}</div>}
                {emailPreview.log.sent_by && <div><strong>From:</strong> {emailPreview.log.sent_by}</div>}
              </div>

              {/* Toggle buttons — only shown when BOTH body_text AND body_html
                  are present (currently only invoice_email sends store both).
                  Lets the operator see either the composed message (their
                  template — bank details, personalised notes) or the invoice
                  HTML that was embedded/attached, without losing either half. */}
              {emailPreview.log.body_text && emailPreview.log.body_html && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Button
                    variant={emailPreviewView === 'message' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEmailPreviewView('message')}
                    data-testid="email-preview-toggle-message"
                  >
                    Message
                  </Button>
                  <Button
                    variant={emailPreviewView === 'invoice' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEmailPreviewView('invoice')}
                    data-testid="email-preview-toggle-invoice"
                  >
                    Invoice
                  </Button>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', alignSelf: 'center', marginLeft: '0.25rem' }}>
                    Both were sent to the customer in the same email.
                  </span>
                </div>
              )}

              {(() => {
                const hasText = !!emailPreview.log.body_text;
                const hasHtml = !!emailPreview.log.body_html;
                // Effective view: if only one exists, force it; otherwise use the toggle state.
                const view = (hasText && hasHtml) ? emailPreviewView : (hasText ? 'message' : (hasHtml ? 'invoice' : null));

                if (view === 'message') {
                  return (
                    <pre
                      data-testid="email-preview-body-text"
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '1rem',
                        background: '#ffffff',
                        maxHeight: '55vh',
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        fontSize: '0.9rem',
                        margin: 0,
                      }}
                    >{emailPreview.log.body_text}</pre>
                  );
                }
                if (view === 'invoice') {
                  return (
                    <div
                      data-testid="email-preview-body-html"
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '1rem',
                        background: '#ffffff',
                        maxHeight: '55vh',
                        overflow: 'auto',
                      }}
                      // The stored HTML is the exact body sent to the customer — render it verbatim.
                      // This is trusted content generated by our own backend templates.
                      dangerouslySetInnerHTML={{ __html: emailPreview.log.body_html }}
                    />
                  );
                }
                return (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px' }}>
                    This email was sent before body-retention was enabled — only metadata is available.
                  </div>
                );
              })()}
            </div>
          )}
          <DialogFooter style={{ marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setEmailPreview({ open: false, log: null })} data-testid="email-preview-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default JobDetail;
